import { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, GraduationCap, Users, FolderKanban,
  Handshake, Newspaper, Timer, Settings, LogOut,
  Bell, Menu, X, Pencil, Trash2, Plus, Eye,
  Clock, CheckCircle2, XCircle, AlertTriangle,
  ChevronRight, ChevronDown, ChevronUp, Search, TrendingUp,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, Heading3, Minus,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Code, FileCode, Undo2, Redo2, Image as ImageIcon, Braces,
  Upload, Library, Copy, Check, Link as LinkIcon,
  UserCog, ScrollText, ShieldCheck, ShieldOff, KeyRound,
  LayoutTemplate, GripVertical, Type, BarChart2, CreditCard, Megaphone,
  MessageSquare, FileText, Star, Building2, GitBranch, ImagePlus,
  Globe, Layers, ExternalLink, CornerDownRight,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { logos } from '../assets';
import * as api from '../api/api';
import './Admin.css';

/* ─── Constants ──────────────────────────────────────────────────── */

const NAV_MAIN = [
  { id: 'Tableau de bord',  label: 'Tableau de bord',  Icon: LayoutDashboard },
  { id: 'Contacts',         label: 'Messages Contact', Icon: MessageSquare },
  { id: 'Projets',          label: 'Cards Projets',    Icon: FolderKanban,  sub: 'liste & homepage' },
  { id: 'Partenaires',      label: 'Partenaires',      Icon: Handshake,     sub: 'logos homepage' },
  { id: 'Actualités',       label: 'Actualités',       Icon: Newspaper },
];
const NAV_MGMT = [
  { id: 'Médiathèque',       label: 'Médiathèque',        Icon: Library },
  { id: 'Utilisateurs',      label: 'Utilisateurs',        Icon: UserCog },
  { id: 'Journal',           label: "Journal d'activité",  Icon: ScrollText },
  { id: 'Compteur FORPRODE', label: 'Compteur FORPRODE',   Icon: Timer },
  { id: 'Paramètres',        label: 'Paramètres',          Icon: Settings },
];
const NAV_CMS = [
  { id: 'Pages',      label: 'Pages',      Icon: LayoutTemplate, sub: 'contenu & sections' },
  { id: 'Navigation', label: 'Navigation', Icon: Globe,          sub: 'menus & sous-menus' },
  { id: 'Footer',     label: 'Footer',     Icon: Layers,         sub: 'pied de page' },
];

const SECTION_TYPES = [
  // ── Sections de base ──
  { type: 'hero',              label: 'Hero',                        Icon: Megaphone,      group: 'base' },
  { type: 'hero_stats',        label: 'Stats animées (hero)',        Icon: BarChart2,      group: 'base' },
  { type: 'text_image',        label: 'Texte + Image',               Icon: Type,           group: 'base' },
  { type: 'stats',             label: 'Statistiques simples',        Icon: BarChart2,      group: 'base' },
  { type: 'cards',             label: 'Cards',                       Icon: CreditCard,     group: 'base' },
  { type: 'cta',               label: 'Call to Action',              Icon: Star,           group: 'base' },
  { type: 'cta_dark',          label: 'CTA sombre',                  Icon: Star,           group: 'base' },
  { type: 'faq',               label: 'FAQ',                         Icon: MessageSquare,  group: 'base' },
  { type: 'rich_text',         label: 'Texte enrichi',               Icon: FileText,       group: 'base' },
  { type: 'timeline',          label: 'Timeline',                    Icon: GitBranch,      group: 'base' },
  { type: 'gallery',           label: 'Galerie',                     Icon: ImagePlus,      group: 'base' },
  // ── Sections spécifiques pages ──
  { type: 'mission_grid',      label: 'Mission & Valeurs',           Icon: Star,           group: 'page' },
  { type: 'team_grid',         label: 'Équipe',                      Icon: Users,          group: 'page' },
  { type: 'partners_block',    label: 'Bloc Partenariats',           Icon: Building2,      group: 'page' },
  { type: 'process_steps',     label: 'Étapes / Processus',          Icon: GitBranch,      group: 'page' },
  { type: 'forprode_banner',   label: 'Bannière FORPRODE',           Icon: Megaphone,      group: 'page' },
  { type: 'about_home',        label: 'À propos (section accueil)',  Icon: Type,           group: 'page' },
  { type: 'entreprises_block', label: 'Section Entreprises',         Icon: Building2,      group: 'page' },
  { type: 'contact_info',      label: 'Informations de contact',     Icon: MessageSquare,  group: 'page' },
  { type: 'domaines_home',     label: "Domaines d'intervention (accueil)", Icon: GraduationCap, group: 'page' },
  { type: 'careers_home',      label: 'Carrières (accueil)',         Icon: TrendingUp,     group: 'page' },
  { type: 'bailleurs_home',    label: 'Bailleurs (accueil)',         Icon: Building2,      group: 'page' },
  { type: 'projets_home',      label: 'Projets (accueil)',           Icon: FolderKanban,   group: 'page' },
  { type: 'temoignages_home',  label: 'Témoignages (accueil)',       Icon: Users,          group: 'page' },
  // ── Sections dynamiques ──
  { type: 'alumni',            label: 'Témoignages Alumni',          Icon: Users,          group: 'dyn' },
  { type: 'partners',          label: 'Bandeau Partenaires',         Icon: Building2,      group: 'dyn' },
];
const SECTION_LABELS = Object.fromEntries(SECTION_TYPES.map(t => [t.type, t.label]));

const STATUT_MAP = {
  toutes:     { label: 'Toutes',     color: '#888',    bg: '#f4f4f4' },
  en_attente: { label: 'En attente', color: '#f59e0b', bg: '#fffbeb' },
  entretien:  { label: 'Entretien',  color: '#3b82f6', bg: '#eff6ff' },
  accepte:    { label: 'Accepté',    color: '#22c55e', bg: '#f0fdf4' },
  refuse:     { label: 'Refusé',     color: '#ef4444', bg: '#fef2f2' },
};

/* ─── Helpers ────────────────────────────────────────────────────── */

const slugify = str =>
  str.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');

/* ─── Micro-composants ───────────────────────────────────────────── */

function Badge({ statut }) {
  const s = STATUT_MAP[statut] || STATUT_MAP.toutes;
  return (
    <span className="adm-badge" style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}30` }}>
      {s.label}
    </span>
  );
}

function Empty({ icon, title, sub, action }) {
  return (
    <div className="adm-empty">
      <div className="adm-empty__icon">{icon}</div>
      <div className="adm-empty__title">{title}</div>
      {sub && <div className="adm-empty__sub">{sub}</div>}
      {action}
    </div>
  );
}

/* ─── MediaCard — carte image médiathèque ───────────────────────── */

function MediaCard({ file }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(file.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="adm-mediacard">
      <div className="adm-mediacard__img-wrap">
        <img src={file.url} alt={file.filename} loading="lazy" />
      </div>
      <div className="adm-mediacard__footer">
        <span className="adm-mediacard__name" title={file.filename}>{file.filename}</span>
        <button className="adm-mediacard__copy" onClick={copy} title="Copier l'URL">
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copié !' : 'Copier URL'}
        </button>
      </div>
    </div>
  );
}

/* ─── MediaPicker — modal sélection / upload image ─────────────── */

function MediaPicker({ open, token, onSelect, onClose }) {
  const [tab, setTab]           = useState('library');
  const [files, setFiles]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.getUploads(token).then(d => setFiles(d?.data || []));
  }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadFile(file, token);
      setFiles(p => [{ url: res.url, filename: res.filename }, ...p]);
      setTab('library');
    } catch (e) {
      alert(e.message);
    } finally {
      setUploading(false);
    }
  };

  const copyUrl = (url, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 1800);
  };

  if (!open) return null;

  return (
    <div className="adm-mpicker-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="adm-mpicker-modal">
        <div className="adm-mpicker-header">
          <span className="adm-mpicker-title">Médiathèque</span>
          <button className="adm-mpicker-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="adm-mpicker-tabs">
          <button className={`adm-mpicker-tab ${tab === 'library' ? 'active' : ''}`} onClick={() => setTab('library')}>
            <Library size={13} /> Mes images
          </button>
          <button className={`adm-mpicker-tab ${tab === 'upload' ? 'active' : ''}`} onClick={() => setTab('upload')}>
            <Upload size={13} /> Uploader
          </button>
        </div>

        {tab === 'upload' && (
          <div className="adm-mpicker-upload">
            <label className="adm-mpicker-dropzone">
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} disabled={uploading} />
              {uploading
                ? <><div className="adm-mpicker-spinner" /><span>Upload en cours…</span></>
                : <><Upload size={28} /><span>Cliquer pour choisir une image</span><span className="adm-mpicker-hint">JPG, PNG, WebP, GIF — max 5 Mo</span></>
              }
            </label>
          </div>
        )}

        {tab === 'library' && (
          <div className="adm-mpicker-grid">
            {files.map(f => (
              <div key={f.filename} className="adm-mpicker-thumb" title={f.filename}
                onClick={() => { onSelect(f.url); onClose(); }}>
                <img src={f.url} alt={f.filename} loading="lazy" />
                <button className="adm-mpicker-copy" onClick={e => copyUrl(f.url, e)} title="Copier l'URL">
                  {copiedUrl === f.url ? <Check size={11} /> : <Copy size={11} />}
                </button>
              </div>
            ))}
            {!files.length && (
              <div className="adm-mpicker-empty">
                <Library size={32} />
                <p>Aucune image uploadée.<br />Utilisez l'onglet "Uploader".</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── RichEditor — TipTap WYSIWYG ───────────────────────────────── */

function RichEditor({ value, onChange, onPickImage }) {
  const [sourceMode, setSourceMode] = useState(false);
  const [rawHtml, setRawHtml]       = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image,
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  if (!editor) return null;

  const insertImage = () => {
    if (onPickImage) {
      onPickImage(url => editor.chain().focus().setImage({ src: url }).run());
    } else {
      const url = window.prompt('URL de l\'image :');
      if (url) editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const toggleSource = () => {
    if (sourceMode) {
      editor.commands.setContent(rawHtml);
      onChange(rawHtml);
    } else {
      setRawHtml(editor.getHTML());
    }
    setSourceMode(p => !p);
  };

  const TOOLS = [
    { title: 'Annuler',          Icon: Undo2,          active: () => false,                                    fn: () => editor.chain().focus().undo().run() },
    { title: 'Rétablir',         Icon: Redo2,          active: () => false,                                    fn: () => editor.chain().focus().redo().run() },
    { sep: true },
    { title: 'Gras',             Icon: Bold,           active: () => editor.isActive('bold'),                  fn: () => editor.chain().focus().toggleBold().run() },
    { title: 'Italique',         Icon: Italic,         active: () => editor.isActive('italic'),                fn: () => editor.chain().focus().toggleItalic().run() },
    { title: 'Souligné',         Icon: UnderlineIcon,  active: () => editor.isActive('underline'),             fn: () => editor.chain().focus().toggleUnderline().run() },
    { sep: true },
    { title: 'Titre H2',         Icon: Heading2,       active: () => editor.isActive('heading', { level: 2 }), fn: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { title: 'Titre H3',         Icon: Heading3,       active: () => editor.isActive('heading', { level: 3 }), fn: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
    { sep: true },
    { title: 'Aligner à gauche', Icon: AlignLeft,      active: () => editor.isActive({ textAlign: 'left' }),   fn: () => editor.chain().focus().setTextAlign('left').run() },
    { title: 'Centrer',          Icon: AlignCenter,    active: () => editor.isActive({ textAlign: 'center' }), fn: () => editor.chain().focus().setTextAlign('center').run() },
    { title: 'Aligner à droite', Icon: AlignRight,     active: () => editor.isActive({ textAlign: 'right' }),  fn: () => editor.chain().focus().setTextAlign('right').run() },
    { title: 'Justifier',        Icon: AlignJustify,   active: () => editor.isActive({ textAlign: 'justify' }),fn: () => editor.chain().focus().setTextAlign('justify').run() },
    { sep: true },
    { title: 'Liste à puces',    Icon: List,           active: () => editor.isActive('bulletList'),            fn: () => editor.chain().focus().toggleBulletList().run() },
    { title: 'Liste numérotée',  Icon: ListOrdered,    active: () => editor.isActive('orderedList'),           fn: () => editor.chain().focus().toggleOrderedList().run() },
    { sep: true },
    { title: 'Code inline',      Icon: Code,           active: () => editor.isActive('code'),                  fn: () => editor.chain().focus().toggleCode().run() },
    { title: 'Bloc de code',     Icon: FileCode,       active: () => editor.isActive('codeBlock'),             fn: () => editor.chain().focus().toggleCodeBlock().run() },
    { sep: true },
    { title: 'Ligne horizontale',Icon: Minus,          active: () => false,                                    fn: () => editor.chain().focus().setHorizontalRule().run() },
    { title: 'Insérer une image',Icon: ImageIcon,      active: () => false,                                    fn: insertImage },
  ];

  return (
    <div className="adm-rich-wrap">
      <div className="adm-rich-toolbar">
        {TOOLS.map((t, i) =>
          t.sep
            ? <span key={i} className="adm-rich-sep" />
            : <button
                key={i}
                type="button"
                title={t.title}
                className={`adm-rich-tool ${t.active() ? 'adm-rich-tool--active' : ''}`}
                onMouseDown={e => { e.preventDefault(); t.fn(); }}
              >
                <t.Icon size={14} />
              </button>
        )}
        <span className="adm-rich-sep" />
        <button
          type="button"
          title="Code source HTML"
          className={`adm-rich-tool ${sourceMode ? 'adm-rich-tool--active' : ''}`}
          onMouseDown={e => { e.preventDefault(); toggleSource(); }}
        >
          <Braces size={14} />
        </button>
      </div>
      {sourceMode ? (
        <textarea
          className="adm-rich-source"
          value={rawHtml}
          onChange={e => { setRawHtml(e.target.value); onChange(e.target.value); }}
          spellCheck={false}
        />
      ) : (
        <EditorContent editor={editor} className="adm-rich-body" />
      )}
    </div>
  );
}

/* ─── ArticleEditor ──────────────────────────────────────────────── */

function ArticleEditor({ article, onSave, onCancel, onPickImage }) {
  const isNew = !article.id;
  const [form, setForm] = useState({
    titre: '', slug: '', resume: '', contenu: '',
    categorie: '', tags: '', image_url: '',
    published_at: new Date().toISOString().slice(0, 10),
    publie: false,
    ...article,
    tags: Array.isArray(article.tags) ? article.tags.join(', ') : (article.tags || ''),
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleTitre = (v) =>
    setForm(p => ({ ...p, titre: v, ...(isNew ? { slug: slugify(v) } : {}) }));

  const handleSubmit = () => {
    if (!form.titre.trim()) { alert('Le titre est requis.'); return; }
    onSave({
      ...form,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      publie: form.publie ? 1 : 0,
    });
  };

  return (
    <div className="adm-card">
      <div className="adm-card__header">
        <button className="adm-back-btn" onClick={onCancel}>← Retour</button>
        <h2 className="adm-card__title">{isNew ? 'Nouvel article' : `Modifier : ${article.titre}`}</h2>
      </div>
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full">
          <label className="adm-label">Titre *</label>
          <input className="adm-input" value={form.titre} onChange={e => handleTitre(e.target.value)} placeholder="Titre de l'article" />
        </div>
        <div className="adm-field">
          <label className="adm-label">Slug (URL)</label>
          <input className="adm-input" value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="mon-article-slug" />
        </div>
        <div className="adm-field">
          <label className="adm-label">Catégorie</label>
          <input className="adm-input" value={form.categorie || ''} onChange={e => set('categorie', e.target.value)} placeholder="IA, Tech, Mobile…" />
        </div>
        <div className="adm-field adm-field--full">
          <label className="adm-label">Extrait</label>
          <textarea className="adm-input" rows={2} value={form.resume || ''} onChange={e => set('resume', e.target.value)} placeholder="Courte description affichée dans les listes" />
        </div>
        <div className="adm-field adm-field--full">
          <label className="adm-label">Contenu (HTML)</label>
          <RichEditor value={form.contenu} onChange={v => set('contenu', v)} onPickImage={onPickImage} />
        </div>
        <div className="adm-field adm-field--full">
          <label className="adm-label">Image à la une</label>
          <div className="adm-input-row">
            <input className="adm-input" value={form.image_url || ''} onChange={e => set('image_url', e.target.value)} placeholder="https://…/image.webp" />
            {onPickImage && (
              <button type="button" className="adm-btn-media" onClick={() => onPickImage(url => set('image_url', url))}>
                <Library size={14} /> Médiathèque
              </button>
            )}
          </div>
          {form.image_url && <img src={form.image_url} alt="" className="adm-img-preview" />}
        </div>
        <div className="adm-field">
          <label className="adm-label">Tags (séparés par virgule)</label>
          <input className="adm-input" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="IA, Afrique, Mobile" />
        </div>
        <div className="adm-field">
          <label className="adm-label">Date de publication</label>
          <input className="adm-input" type="date" value={(form.published_at || '').slice(0, 10)} onChange={e => set('published_at', e.target.value)} />
        </div>
        <div className="adm-field">
          <label className="adm-label">Statut de publication</label>
          <label className="adm-toggle">
            <input type="checkbox" checked={!!form.publie} onChange={e => set('publie', e.target.checked)} />
            <span className="adm-toggle__track" />
            <span className="adm-toggle__label">{form.publie ? '✓ Publié' : 'Brouillon'}</span>
          </label>
        </div>
      </div>
      <div className="adm-card__actions">
        <button className="adm-btn-primary" onClick={handleSubmit}>
          {isNew ? "Créer l'article" : 'Enregistrer les modifications'}
        </button>
        <button className="adm-btn-outline" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

/* ─── UserForm ───────────────────────────────────────────────────── */

function UserForm({ user, onSave, onCancel }) {
  const isNew = !user.id;
  const [form, setForm] = useState({
    nom: '', email: '', role: 'admin', password: '', password2: '',
    ...user,
  });
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = () => {
    setErr('');
    if (!form.email.trim()) { setErr('Email requis.'); return; }
    if (isNew && !form.password) { setErr('Mot de passe requis.'); return; }
    if (form.password && form.password !== form.password2) { setErr('Les mots de passe ne correspondent pas.'); return; }
    const payload = { nom: form.nom, email: form.email, role: form.role };
    if (form.password) payload.password = form.password;
    if (user.id) payload.id = user.id;
    onSave(payload);
  };

  return (
    <div className="adm-card adm-card--narrow">
      <div className="adm-card__header">
        <button className="adm-back-btn" onClick={onCancel}>← Retour</button>
        <h2 className="adm-card__title">{isNew ? 'Nouvel administrateur' : `Modifier : ${user.nom || user.email}`}</h2>
      </div>
      <div className="adm-form-grid">
        <div className="adm-field">
          <label className="adm-label">Nom</label>
          <input className="adm-input" value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="Prénom Nom" />
        </div>
        <div className="adm-field">
          <label className="adm-label">Rôle</label>
          <select className="adm-input" value={form.role} onChange={e => set('role', e.target.value)}>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <div className="adm-field adm-field--full">
          <label className="adm-label">Email *</label>
          <input className="adm-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="admin@exemple.com" />
        </div>
        <div className="adm-field">
          <label className="adm-label">{isNew ? 'Mot de passe *' : 'Nouveau mot de passe'}</label>
          <div className="adm-input-row">
            <span className="adm-input-icon"><KeyRound size={14} /></span>
            <input className="adm-input adm-input--icon" type="password" value={form.password} onChange={e => set('password', e.target.value)}
              placeholder={isNew ? 'Choisir un mot de passe' : 'Laisser vide pour ne pas changer'} />
          </div>
        </div>
        <div className="adm-field">
          <label className="adm-label">Confirmer le mot de passe</label>
          <div className="adm-input-row">
            <span className="adm-input-icon"><KeyRound size={14} /></span>
            <input className="adm-input adm-input--icon" type="password" value={form.password2} onChange={e => set('password2', e.target.value)} placeholder="Répéter le mot de passe" />
          </div>
        </div>
        {err && <div className="adm-field--full" style={{ color:'#ef4444', fontSize:13, padding:'4px 0' }}>{err}</div>}
      </div>
      <div className="adm-card__actions">
        <button className="adm-btn-primary" onClick={handleSubmit}>
          {isNew ? 'Créer le compte' : 'Enregistrer'}
        </button>
        <button className="adm-btn-outline" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

/* ─── NavItemRow — ligne d'un item de navigation (éditable) ─────── */

function NavItemRow({ item, onSave, onDelete, onMoveUp, onMoveDown, isFirst, isLast, level = 0, onAddChild }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ label: item.label, href: item.href });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    onSave(item.id, form);
    setEditing(false);
  };

  return (
    <div>
      <div className={`adm-nav-edit-row ${level > 0 ? 'adm-nav-edit-row--child' : ''} ${!item.actif ? 'adm-nav-edit-row--inactive' : ''}`}>
        {level > 0 && <CornerDownRight size={13} className="adm-nav-child-icon" />}

        {editing ? (
          <div className="adm-nav-edit-form">
            <input className="adm-input" value={form.label} onChange={e => set('label', e.target.value)} placeholder="Label" autoFocus />
            <input className="adm-input" value={form.href} onChange={e => set('href', e.target.value)} placeholder="/lien" />
            <button className="adm-btn-primary adm-btn--sm" onClick={handleSave}>OK</button>
            <button className="adm-btn-outline adm-btn--sm" onClick={() => { setEditing(false); setForm({ label: item.label, href: item.href }); }}>✕</button>
          </div>
        ) : (
          <>
            <span className="adm-nav-item-label">{item.label}</span>
            <span className="adm-nav-item-href">
              <ExternalLink size={11} style={{ marginRight: 3, opacity: 0.5 }} />{item.href}
            </span>
            <div className="adm-sec-row__spacer" />
            <div className="adm-sec-row__actions">
              <button className="adm-icon-btn" onClick={onMoveUp} disabled={isFirst} title="Monter"><ChevronUp size={14} /></button>
              <button className="adm-icon-btn" onClick={onMoveDown} disabled={isLast} title="Descendre"><ChevronDown size={14} /></button>
              <label className="adm-toggle adm-toggle--sm" title={item.actif ? 'Désactiver' : 'Activer'}>
                <input type="checkbox" checked={!!item.actif} onChange={() => onSave(item.id, { actif: item.actif ? 0 : 1 })} />
                <span className="adm-toggle__track" />
              </label>
              <button className="adm-icon-btn" onClick={() => setEditing(true)} title="Modifier"><Pencil size={14} /></button>
              {level === 0 && onAddChild && (
                <button className="adm-icon-btn" onClick={() => onAddChild(item.id)} title="Ajouter sous-menu">
                  <CornerDownRight size={14} />
                </button>
              )}
              <button className="adm-icon-btn adm-icon-btn--danger" onClick={() => onDelete(item.id)} title="Supprimer"><Trash2 size={14} /></button>
            </div>
          </>
        )}
      </div>

      {/* Sous-menus */}
      {item.children?.length > 0 && (
        <div className="adm-nav-children">
          {item.children.map((child, ci) => (
            <NavItemRow
              key={child.id}
              item={child}
              level={1}
              onSave={onSave}
              onDelete={onDelete}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
              isFirst={ci === 0}
              isLast={ci === item.children.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── AddNavForm — mini formulaire ajout item de navigation ─────── */

function AddNavForm({ onSubmit, onCancel }) {
  const [label, setLabel] = useState('');
  const [href, setHref]   = useState('');
  return (
    <div className="adm-nav-edit-form">
      <input className="adm-input" value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (ex: Contact)" autoFocus />
      <input className="adm-input" value={href}  onChange={e => setHref(e.target.value)}  placeholder="/lien (ex: /contact)" />
      <button className="adm-btn-primary adm-btn--sm" onClick={() => onSubmit({ label, href })}>Ajouter</button>
      <button className="adm-btn-outline adm-btn--sm" onClick={onCancel}>Annuler</button>
    </div>
  );
}

/* ─── FooterFormByType — formulaire footer par type de section ───── */

function FooterFormByType({ type, content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });

  switch (type) {
    case 'brand': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Nom</label>
          <input className="adm-input" value={content.name || ''} onChange={e => set('name', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Tagline</label>
          <input className="adm-input" value={content.tagline || ''} onChange={e => set('tagline', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Logo (URL)</label>
          <input className="adm-input" value={content.logo || ''} onChange={e => set('logo', e.target.value)} placeholder="URL ou vide" /></div>
      </div>
    );
    case 'links': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre de colonne</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Liens</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'label',label:'Texte'},{key:'href',label:'Lien'}]}
            onChange={items => set('items', items)} addLabel="Ajouter un lien" /></div>
      </div>
    );
    case 'contact': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Email</label>
          <input className="adm-input" type="email" value={content.email || ''} onChange={e => set('email', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Téléphone</label>
          <input className="adm-input" value={content.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Adresse</label>
          <input className="adm-input" value={content.address || ''} onChange={e => set('address', e.target.value)} /></div>
      </div>
    );
    case 'social': return (
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full"><label className="adm-label">Réseaux sociaux</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'label',label:'Réseau'},{key:'href',label:'URL'},{key:'icon',label:'Icône (facebook/linkedin/twitter)'}]}
            onChange={items => set('items', items)} addLabel="Ajouter un réseau" /></div>
      </div>
    );
    case 'legal': return (
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full"><label className="adm-label">Mention copyright</label>
          <input className="adm-input" value={content.copyright || ''} onChange={e => set('copyright', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Liens légaux</label>
          <RepeatableItems items={content.links || []}
            fields={[{key:'label',label:'Texte'},{key:'href',label:'Lien'}]}
            onChange={links => set('links', links)} addLabel="Ajouter un lien" /></div>
      </div>
    );
    case 'newsletter': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Texte</label>
          <input className="adm-input" value={content.text || ''} onChange={e => set('text', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Placeholder champ email</label>
          <input className="adm-input" value={content.placeholder || ''} onChange={e => set('placeholder', e.target.value)} /></div>
      </div>
    );
    default: return (
      <div className="adm-field adm-field--full">
        <label className="adm-label">Contenu JSON brut</label>
        <textarea className="adm-rich-source" rows={6}
          defaultValue={JSON.stringify(content, null, 2)}
          onBlur={e => { try { onChange(JSON.parse(e.target.value)); } catch {} }}
        />
      </div>
    );
  }
}

/* ─── FooterSectionRow — section footer éditable ────────────────── */

const FOOTER_TYPE_LABELS = {
  brand: 'Marque', links: 'Liens', contact: 'Contact',
  social: 'Réseaux sociaux', legal: 'Mentions légales', newsletter: 'Newsletter',
};

function FooterSectionRow({ section, expanded, onToggle, onSave, onDelete, onMoveUp, onMoveDown, onToggleActif, isFirst, isLast }) {
  const [localContent, setLocalContent] = useState(section.content || {});
  useEffect(() => { setLocalContent(section.content || {}); }, [section.id]);

  const rowLabel = section.type === 'links'
    ? (section.content?.title || 'Liens')
    : (FOOTER_TYPE_LABELS[section.type] || section.type);

  return (
    <div className={`adm-sec-row ${!section.actif ? 'adm-sec-row--inactive' : ''}`}>
      <div className="adm-sec-row__header">
        <span className="adm-sec-type-badge" style={{ background: '#fef3c7', color: '#92400e' }}>
          {FOOTER_TYPE_LABELS[section.type] || section.type}
        </span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginLeft: '8px' }}>{rowLabel}</span>
        <div className="adm-sec-row__spacer" />
        <div className="adm-sec-row__actions">
          <button className="adm-icon-btn" onClick={onMoveUp} disabled={isFirst} title="Monter"><ChevronUp size={14} /></button>
          <button className="adm-icon-btn" onClick={onMoveDown} disabled={isLast} title="Descendre"><ChevronDown size={14} /></button>
          <label className="adm-toggle adm-toggle--sm">
            <input type="checkbox" checked={!!section.actif} onChange={onToggleActif} />
            <span className="adm-toggle__track" />
          </label>
          <button className="adm-icon-btn adm-icon-btn--danger" onClick={onDelete} title="Supprimer section">
            <Trash2 size={14} />
          </button>
          <button className={`adm-icon-btn ${expanded ? 'adm-icon-btn--active' : ''}`} onClick={onToggle} title="Éditer">
            <Pencil size={14} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="adm-sec-form">
          <FooterFormByType type={section.type} content={localContent} onChange={setLocalContent} />
          <div className="adm-card__actions" style={{ marginTop: 16 }}>
            <button className="adm-btn-primary" onClick={() => onSave(section.id, localContent)}>
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── RepeatableItems — champs répétables (stats, cards, faq…) ──── */

function RepeatableItems({ items, fields, onChange, addLabel }) {
  const empty = () => Object.fromEntries(fields.map(f => [f.key, '']));
  const add    = () => onChange([...(items || []), empty()]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(items.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  return (
    <div className="adm-repeat-wrap">
      {(items || []).map((item, i) => (
        <div key={i} className="adm-repeat-item">
          <div className="adm-form-grid">
            {fields.map(f => (
              <div key={f.key} className={`adm-field ${f.full ? 'adm-field--full' : ''}`}>
                <label className="adm-label">{f.label}</label>
                {f.type === 'textarea'
                  ? <textarea className="adm-input" rows={2} value={item[f.key] || ''}
                      onChange={e => update(i, f.key, e.target.value)} />
                  : <input className="adm-input" value={item[f.key] || ''}
                      onChange={e => update(i, f.key, e.target.value)} placeholder={f.placeholder || ''} />
                }
              </div>
            ))}
          </div>
          <button type="button" className="adm-repeat-remove" onClick={() => remove(i)}>
            <Trash2 size={12} /> Supprimer
          </button>
        </div>
      ))}
      <button type="button" className="adm-btn-outline adm-btn--sm" onClick={add}>
        <Plus size={13} /> {addLabel}
      </button>
    </div>
  );
}

/* ─── SectionFormByType — formulaire par type de section ─────────── */

function SectionFormByType({ type, content, onChange, onPickImage }) {
  const set = (k, v) => onChange({ ...content, [k]: v });

  const imgField = (key, label) => (
    <div className="adm-field adm-field--full">
      <label className="adm-label">{label}</label>
      <div className="adm-input-row">
        <input className="adm-input" value={content[key] || ''} onChange={e => set(key, e.target.value)} placeholder="URL image ou vide" />
        {onPickImage && (
          <button type="button" className="adm-btn-media" onClick={() => onPickImage(url => set(key, url))}>
            <Library size={14} /> Médiathèque
          </button>
        )}
      </div>
      {content[key] && <img src={content[key]} alt="" className="adm-img-preview--small" />}
    </div>
  );

  switch (type) {
    case 'hero': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Texte eyebrow (petite ligne au-dessus)</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre (partie normale)</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre (partie dégradée verte)</label>
          <input className="adm-input" value={content.title_grad || ''} onChange={e => set('title_grad', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre</label>
          <textarea className="adm-input" rows={2} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Texte</label>
          <input className="adm-input" value={content.cta_label || ''} onChange={e => set('cta_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Lien</label>
          <input className="adm-input" value={content.cta_href || ''} onChange={e => set('cta_href', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Texte</label>
          <input className="adm-input" value={content.cta2_label || ''} onChange={e => set('cta2_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Lien</label>
          <input className="adm-input" value={content.cta2_href || ''} onChange={e => set('cta2_href', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Onglets de filtre (optionnel)</label>
          <RepeatableItems items={content.filter_tabs || []}
            fields={[{key:'key',label:'Clé (ancre #)'},{key:'label',label:'Label affiché'}]}
            onChange={items => set('filter_tabs', items)} addLabel="Ajouter un onglet" /></div>
        {imgField('bg_image', 'Image de fond (optionnel)')}
      </div>
    );

    case 'text_image': return (
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Texte (HTML)</label>
          <RichEditor value={content.text || ''} onChange={v => set('text', v)} onPickImage={onPickImage} /></div>
        {imgField('image', 'Image')}
        <div className="adm-field"><label className="adm-label">Position de l'image</label>
          <div style={{ display:'flex', gap:16, marginTop:4 }}>
            {['left','right'].map(side => (
              <label key={side} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                <input type="radio" value={side} checked={(content.image_side || 'right') === side}
                  onChange={() => set('image_side', side)} />
                {side === 'left' ? 'Gauche' : 'Droite'}
              </label>
            ))}
          </div>
        </div>
      </div>
    );

    case 'stats': return (
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full"><label className="adm-label">Statistiques</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'value',label:'Valeur'},{key:'label',label:'Libellé'}]}
            onChange={items => set('items', items)} addLabel="Ajouter une stat" /></div>
      </div>
    );

    case 'cards': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre de section</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Sous-titre</label>
          <input className="adm-input" value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Cards</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'title',label:'Titre'},{key:'text',label:'Texte',type:'textarea',full:true},{key:'icon',label:'Icône'},{key:'href',label:'Lien'}]}
            onChange={items => set('items', items)} addLabel="Ajouter une card" /></div>
      </div>
    );

    case 'cta': return (
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Texte</label>
          <textarea className="adm-input" rows={2} value={content.text || ''} onChange={e => set('text', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Texte</label>
          <input className="adm-input" value={content.cta_label || ''} onChange={e => set('cta_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Lien</label>
          <input className="adm-input" value={content.cta_href || ''} onChange={e => set('cta_href', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Texte</label>
          <input className="adm-input" value={content.cta2_label || ''} onChange={e => set('cta2_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Lien</label>
          <input className="adm-input" value={content.cta2_href || ''} onChange={e => set('cta2_href', e.target.value)} /></div>
      </div>
    );

    case 'faq': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre de section</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Questions / Réponses</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'q',label:'Question',full:true},{key:'a',label:'Réponse',type:'textarea',full:true}]}
            onChange={items => set('items', items)} addLabel="Ajouter une question" /></div>
      </div>
    );

    case 'rich_text': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre (optionnel)</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Contenu (HTML)</label>
          <RichEditor value={content.content || ''} onChange={v => set('content', v)} onPickImage={onPickImage} /></div>
      </div>
    );

    case 'alumni': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Témoignages</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'name',label:'Nom'},{key:'role',label:'Poste'},{key:'company',label:'Entreprise'},{key:'quote',label:'Citation',type:'textarea',full:true},{key:'formation',label:'Formation'},{key:'image',label:'Image URL'}]}
            onChange={items => set('items', items)} addLabel="Ajouter un témoignage" /></div>
      </div>
    );

    case 'partners': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Sous-titre</label>
          <input className="adm-input" value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
      </div>
    );

    case 'timeline': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Étapes</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'date',label:'Date / Année'},{key:'title',label:'Titre'},{key:'text',label:'Description',type:'textarea',full:true}]}
            onChange={items => set('items', items)} addLabel="Ajouter une étape" /></div>
      </div>
    );

    case 'gallery': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Images</label>
          <RepeatableItems
            items={(content.images || []).map(url => ({ url }))}
            fields={[{key:'url',label:'URL image',full:true}]}
            onChange={items => set('images', items.map(i => i.url))}
            addLabel="Ajouter une image" /></div>
      </div>
    );

    case 'hero_stats': return (
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full"><label className="adm-label">Cartes animées (num + libellé + barre %)</label>
          <RepeatableItems items={content.items || []}
            fields={[{key:'num',label:'Valeur affichée (ex: 500+)'},{key:'label',label:'Libellé'},{key:'bar',label:'Largeur barre (ex: 85%)'}]}
            onChange={items => set('items', items)} addLabel="Ajouter une carte" /></div>
      </div>
    );

    case 'mission_grid': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre h2 (ligne 1)</label>
          <input className="adm-input" value={content.h2 || ''} onChange={e => set('h2', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre h2 (ligne 2 — sous le br)</label>
          <input className="adm-input" value={content.h2_br || ''} onChange={e => set('h2_br', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Paragraphe 1</label>
          <textarea className="adm-input" rows={3} value={content.p1 || ''} onChange={e => set('p1', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Paragraphe 2</label>
          <textarea className="adm-input" rows={3} value={content.p2 || ''} onChange={e => set('p2', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton — Texte</label>
          <input className="adm-input" value={content.cta_label || ''} onChange={e => set('cta_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton — Lien</label>
          <input className="adm-input" value={content.cta_href || ''} onChange={e => set('cta_href', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Icônes valeurs (4 cartes)</label>
          <RepeatableItems items={content.icons || []}
            fields={[{key:'title',label:'Titre'},{key:'desc',label:'Description',type:'textarea',full:true}]}
            onChange={items => set('icons', items)} addLabel="Ajouter une icône" /></div>
      </div>
    );

    case 'team_grid': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre h2</label>
          <input className="adm-input" value={content.h2 || ''} onChange={e => set('h2', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre</label>
          <input className="adm-input" value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Membres de l'équipe</label>
          <RepeatableItems items={content.members || []}
            fields={[{key:'name',label:'Nom / Groupe'},{key:'role',label:'Rôle'},{key:'desc',label:'Description',type:'textarea',full:true}]}
            onChange={items => set('members', items)} addLabel="Ajouter un membre" /></div>
      </div>
    );

    case 'partners_block': return (
      <div className="adm-form-grid">
        <div className="adm-field adm-field--full"><label className="adm-label">Titre du bloc sombre (Sira-Labs)</label>
          <textarea className="adm-input" rows={2} value={content.box_title || ''} onChange={e => set('box_title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Description du bloc sombre</label>
          <textarea className="adm-input" rows={3} value={content.box_desc || ''} onChange={e => set('box_desc', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Tags pays (séparés par virgule)</label>
          <input className="adm-input" value={content.country_tags || ''} onChange={e => set('country_tags', e.target.value)} placeholder="Côte d'Ivoire,Sénégal,Afrique de l'Ouest" /></div>
        <div className="adm-field"><label className="adm-label">Partenariat académique — Eyebrow</label>
          <input className="adm-input" value={content.academic_eyebrow || ''} onChange={e => set('academic_eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Partenariat académique — Titre (ligne 1)</label>
          <input className="adm-input" value={content.academic_h2 || ''} onChange={e => set('academic_h2', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Partenariat académique — Titre (ligne 2)</label>
          <input className="adm-input" value={content.academic_h2_end || ''} onChange={e => set('academic_h2_end', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Paragraphe 1</label>
          <textarea className="adm-input" rows={3} value={content.academic_p1 || ''} onChange={e => set('academic_p1', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Paragraphe 2</label>
          <textarea className="adm-input" rows={3} value={content.academic_p2 || ''} onChange={e => set('academic_p2', e.target.value)} /></div>
      </div>
    );

    case 'cta_dark': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Titre (partie normale)</label>
          <input className="adm-input" value={content.h2 || ''} onChange={e => set('h2', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre (partie dégradée verte)</label>
          <input className="adm-input" value={content.h2_grad || ''} onChange={e => set('h2_grad', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre / texte</label>
          <textarea className="adm-input" rows={2} value={content.p || ''} onChange={e => set('p', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Texte</label>
          <input className="adm-input" value={content.cta1_label || ''} onChange={e => set('cta1_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Lien</label>
          <input className="adm-input" value={content.cta1_href || ''} onChange={e => set('cta1_href', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Texte</label>
          <input className="adm-input" value={content.cta2_label || ''} onChange={e => set('cta2_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Lien</label>
          <input className="adm-input" value={content.cta2_href || ''} onChange={e => set('cta2_href', e.target.value)} /></div>
      </div>
    );

    case 'process_steps': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre h2</label>
          <input className="adm-input" value={content.h2 || ''} onChange={e => set('h2', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre / intro</label>
          <textarea className="adm-input" rows={2} value={content.p || ''} onChange={e => set('p', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Étapes</label>
          <RepeatableItems items={content.steps || []}
            fields={[{key:'num',label:'Numéro (ex: 01)'},{key:'title',label:'Titre'},{key:'text',label:'Description',type:'textarea',full:true}]}
            onChange={items => set('steps', items)} addLabel="Ajouter une étape" /></div>
      </div>
    );

    case 'forprode_banner': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Badge (ex: Programme phare)</label>
          <input className="adm-input" value={content.badge || ''} onChange={e => set('badge', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre (partie normale)</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre (partie dégradée verte)</label>
          <input className="adm-input" value={content.title_grad || ''} onChange={e => set('title_grad', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Description</label>
          <textarea className="adm-input" rows={3} value={content.description || ''} onChange={e => set('description', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton — Texte</label>
          <input className="adm-input" value={content.cta_label || ''} onChange={e => set('cta_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton — Lien</label>
          <input className="adm-input" value={content.cta_href || ''} onChange={e => set('cta_href', e.target.value)} /></div>
      </div>
    );

    case 'about_home': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 1</label>
          <input className="adm-input" value={content.h2 || ''} onChange={e => set('h2', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 2</label>
          <input className="adm-input" value={content.h2_line2 || ''} onChange={e => set('h2_line2', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 3</label>
          <input className="adm-input" value={content.h2_line3 || ''} onChange={e => set('h2_line3', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre / texte intro</label>
          <textarea className="adm-input" rows={3} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Points clés (piliers)</label>
          <RepeatableItems items={(content.piliers || []).map(p => typeof p === 'string' ? { text: p } : p)}
            fields={[{key:'text',label:'Texte du point',type:'textarea',full:true}]}
            onChange={items => set('piliers', items.map(i => i.text))} addLabel="Ajouter un point" /></div>
      </div>
    );

    case 'entreprises_block': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre h2 — ligne 1</label>
          <input className="adm-input" value={content.h2 || ''} onChange={e => set('h2', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre h2 — ligne 2</label>
          <input className="adm-input" value={content.h2_line2 || ''} onChange={e => set('h2_line2', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Paragraphe intro</label>
          <textarea className="adm-input" rows={3} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Avantages (points clés)</label>
          <RepeatableItems items={(content.advantages || []).map(a => typeof a === 'string' ? { text: a } : a)}
            fields={[{key:'text',label:'Texte',type:'textarea',full:true}]}
            onChange={items => set('advantages', items.map(i => i.text))} addLabel="Ajouter un avantage" /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Statistiques clés</label>
          <RepeatableItems items={content.stats || []}
            fields={[{key:'value',label:'Valeur'},{key:'label',label:'Libellé'}]}
            onChange={items => set('stats', items)} addLabel="Ajouter une stat" /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Texte</label>
          <input className="adm-input" value={content.cta1_label || ''} onChange={e => set('cta1_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton principal — Lien</label>
          <input className="adm-input" value={content.cta1_href || ''} onChange={e => set('cta1_href', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Texte</label>
          <input className="adm-input" value={content.cta2_label || ''} onChange={e => set('cta2_label', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Bouton secondaire — Lien</label>
          <input className="adm-input" value={content.cta2_href || ''} onChange={e => set('cta2_href', e.target.value)} /></div>
      </div>
    );

    case 'contact_info': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Email</label>
          <input className="adm-input" value={content.email || ''} onChange={e => set('email', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Téléphone</label>
          <input className="adm-input" value={content.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Localisation</label>
          <input className="adm-input" value={content.location || ''} onChange={e => set('location', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre horaires</label>
          <input className="adm-input" value={content.hours_title || ''} onChange={e => set('hours_title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Horaires (ligne 1)</label>
          <input className="adm-input" value={content.hours_line1 || ''} onChange={e => set('hours_line1', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Horaires (ligne 2 — délai de réponse)</label>
          <input className="adm-input" value={content.hours_line2 || ''} onChange={e => set('hours_line2', e.target.value)} /></div>
      </div>
    );

    case 'formations_home': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 1</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 2</label>
          <input className="adm-input" value={content.title_line2 || ''} onChange={e => set('title_line2', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre</label>
          <textarea className="adm-input" rows={2} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Card sur-mesure — Titre</label>
          <input className="adm-input" value={content.custom_title || ''} onChange={e => set('custom_title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Card sur-mesure — Description</label>
          <textarea className="adm-input" rows={2} value={content.custom_desc || ''} onChange={e => set('custom_desc', e.target.value)} /></div>
      </div>
    );

    case 'careers_home': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 1</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 2</label>
          <input className="adm-input" value={content.title_line2 || ''} onChange={e => set('title_line2', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre</label>
          <textarea className="adm-input" rows={2} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Statistiques (chiffres clés)</label>
          <RepeatableItems items={content.stats || []}
            fields={[{key:'val',label:'Valeur (ex: 3x)'},{key:'lbl',label:'Libellé',full:true}]}
            onChange={items => set('stats', items)} addLabel="Ajouter une stat" /></div>
        <div className="adm-field"><label className="adm-label">Encadré recrutement — Titre</label>
          <input className="adm-input" value={content.cta_title || ''} onChange={e => set('cta_title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Encadré recrutement — Description</label>
          <textarea className="adm-input" rows={2} value={content.cta_desc || ''} onChange={e => set('cta_desc', e.target.value)} /></div>
      </div>
    );

    case 'bailleurs_home': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 1</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field"><label className="adm-label">Titre — ligne 2</label>
          <input className="adm-input" value={content.title_line2 || ''} onChange={e => set('title_line2', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre</label>
          <textarea className="adm-input" rows={2} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Texte du bouton CTA</label>
          <input className="adm-input" value={content.cta_label || ''} onChange={e => set('cta_label', e.target.value)} /></div>
      </div>
    );

    case 'projets_home': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Sous-titre</label>
          <textarea className="adm-input" rows={2} value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></div>
      </div>
    );

    case 'temoignages_home': return (
      <div className="adm-form-grid">
        <div className="adm-field"><label className="adm-label">Eyebrow</label>
          <input className="adm-input" value={content.eyebrow || ''} onChange={e => set('eyebrow', e.target.value)} /></div>
        <div className="adm-field adm-field--full"><label className="adm-label">Titre</label>
          <input className="adm-input" value={content.title || ''} onChange={e => set('title', e.target.value)} /></div>
      </div>
    );

    default: return (
      <div className="adm-field adm-field--full">
        <label className="adm-label">Contenu JSON brut</label>
        <textarea className="adm-rich-source" rows={8}
          defaultValue={JSON.stringify(content, null, 2)}
          onBlur={e => { try { onChange(JSON.parse(e.target.value)); } catch {} }}
        />
      </div>
    );
  }
}

/* ─── SortableSectionRow — ligne section draggable ───────────────── */

function SortableSectionRow({ section, expanded, onToggle, onSave, onDelete, onMoveUp, onMoveDown, onToggleActif, isFirst, isLast, onPickImage }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const [localContent, setLocalContent] = useState(section.content || {});

  // Sync if section changes externally
  useEffect(() => { setLocalContent(section.content || {}); }, [section.id]);

  return (
    <div ref={setNodeRef} style={style} className={`adm-sec-row ${isDragging ? 'adm-sec-row--dragging' : ''} ${!section.actif ? 'adm-sec-row--inactive' : ''}`}>
      <div className="adm-sec-row__header">
        <span className="adm-drag-handle" {...attributes} {...listeners} title="Glisser pour réordonner">
          <GripVertical size={16} />
        </span>
        <span className="adm-sec-type-badge">{section.label || SECTION_LABELS[section.type] || section.type}</span>
        <div className="adm-sec-row__spacer" />
        <div className="adm-sec-row__actions">
          <button title="Monter" className="adm-icon-btn" onClick={onMoveUp} disabled={isFirst}><ChevronUp size={14} /></button>
          <button title="Descendre" className="adm-icon-btn" onClick={onMoveDown} disabled={isLast}><ChevronDown size={14} /></button>
          <label className="adm-toggle adm-toggle--sm" title={section.actif ? 'Désactiver' : 'Activer'}>
            <input type="checkbox" checked={!!section.actif} onChange={onToggleActif} />
            <span className="adm-toggle__track" />
          </label>
          <button title="Éditer" className={`adm-icon-btn ${expanded ? 'adm-icon-btn--active' : ''}`} onClick={onToggle}>
            <Pencil size={14} />
          </button>
          <button title="Supprimer" className="adm-icon-btn adm-icon-btn--danger" onClick={onDelete}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="adm-sec-form">
          <SectionFormByType
            type={section.type}
            content={localContent}
            onChange={setLocalContent}
            onPickImage={onPickImage}
          />
          <div className="adm-card__actions" style={{ marginTop: 16 }}>
            <button className="adm-btn-primary" onClick={() => onSave(section.id, localContent)}>
              Enregistrer la section
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Login ──────────────────────────────────────────────────────── */

function LoginScreen({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.login(email, password);
      localStorage.setItem('tta_admin_token', res.token);
      localStorage.setItem('tta_admin_info', JSON.stringify(res.admin));
      onLogin(res.token, res.admin);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-login">
      <div className="adm-login__card">
        <div className="adm-login__header">
          <img src={logos.dark} alt="TTA" className="adm-login__logo" />
          <span className="adm-login__badge">Espace Admin</span>
        </div>
        <div className="adm-login__body">
          <h1 className="adm-login__title">Connexion</h1>
          <p className="adm-login__sub">Accédez au tableau de bord d'administration</p>
          <form onSubmit={handleSubmit}>
            <div className="adm-field">
              <label className="adm-label">Email</label>
              <input
                className="adm-input" type="email" required autoFocus
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder="admin@techtalentaccelerator.com"
              />
            </div>
            <div className="adm-field">
              <label className="adm-label">Mot de passe</label>
              <input
                className="adm-input" type="password" required
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div className="adm-error-box">
                <AlertTriangle size={14} />
                <span>{error}</span>
              </div>
            )}
            <button className="adm-btn-primary adm-btn--full" disabled={loading}>
              {loading ? 'Connexion en cours…' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ─── Sidebar ────────────────────────────────────────────────────── */

function Sidebar({ section, setSection, admin, onLogout, mobileOpen, onClose }) {
  return (
    <>
      {mobileOpen && <div className="adm-overlay" onClick={onClose} />}
      <aside className={`adm-sidebar ${mobileOpen ? 'adm-sidebar--open' : ''}`}>

        <div className="adm-sidebar__logo">
          <img src={logos.dark} alt="TTA" style={{ height: '26px' }} />
          <span>ADMIN</span>
          <button className="adm-sidebar__close-btn" onClick={onClose}><X size={15} /></button>
        </div>

        <nav className="adm-sidebar__nav">
          <div className="adm-nav-label">Principal</div>
          {NAV_MAIN.map(({ id, label, sub, Icon }) => (
            <button
              key={id}
              className={`adm-nav-item ${section === id ? 'active' : ''}`}
              onClick={() => { setSection(id); onClose(); }}
            >
              <Icon size={16} />
              <span className="adm-nav-item__text">
                {label}
                {sub && <span className="adm-nav-item__sub">{sub}</span>}
              </span>
            </button>
          ))}
          <div className="adm-nav-label">CMS</div>
          {NAV_CMS.map(({ id, label, sub, Icon }) => (
            <button
              key={id}
              className={`adm-nav-item ${section === id ? 'active' : ''}`}
              onClick={() => { setSection(id); onClose(); }}
            >
              <Icon size={16} />
              <span className="adm-nav-item__text">
                {label}
                {sub && <span className="adm-nav-item__sub">{sub}</span>}
              </span>
            </button>
          ))}
          <div className="adm-nav-label">Gestion</div>
          {NAV_MGMT.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`adm-nav-item ${section === id ? 'active' : ''}`}
              onClick={() => { setSection(id); onClose(); }}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="adm-sidebar__footer">
          <a href="/" target="_blank" rel="noreferrer" className="adm-nav-item adm-nav-item--muted">
            <Eye size={15} />
            <span>Voir le site</span>
          </a>
          <button className="adm-nav-item adm-nav-item--danger" onClick={onLogout}>
            <LogOut size={15} />
            <span>Déconnexion</span>
          </button>
          <div className="adm-sidebar__user">
            <div className="adm-sidebar__avatar">
              {(admin?.nom || 'A')[0].toUpperCase()}
            </div>
            <div>
              <div className="adm-sidebar__uname">{admin?.nom || 'Admin'}</div>
              <div className="adm-sidebar__urole">
                {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
              </div>
            </div>
          </div>
        </div>

      </aside>
    </>
  );
}

/* ─── Topbar ─────────────────────────────────────────────────────── */

function Topbar({ section, admin, onMenu }) {
  return (
    <header className="adm-topbar">
      <div className="adm-topbar__left">
        <button className="adm-topbar__menu-btn" onClick={onMenu}>
          <Menu size={20} />
        </button>
        <nav className="adm-breadcrumb">
          <span>Accueil</span>
          <ChevronRight size={12} />
          <span className="adm-breadcrumb__active">{section}</span>
        </nav>
      </div>
      <div className="adm-topbar__right">
        <div className="adm-topbar__search">
          <Search size={13} />
          <input placeholder="Rechercher…" />
        </div>
        <button className="adm-topbar__icon-btn">
          <Bell size={17} />
        </button>
        <div className="adm-topbar__profile">
          <div className="adm-topbar__avatar">
            {(admin?.nom || 'A')[0].toUpperCase()}
          </div>
          <span>{admin?.nom || admin?.email || 'Admin'}</span>
          <ChevronDown size={13} />
        </div>
      </div>
    </header>
  );
}

/* ─── CmsFormationsEditor ────────────────────────────────────────── */

function CmsFormationsEditor({ editCmsFormation, setEditCmsFormation, formations, saveCmsFormation, staticFormationsData }) {
  const [cmsFTab, setCmsFTab] = useState(0);

  const dbIds    = formations.map(f => f.id);
  const staticOnly = staticFormationsData
    .filter(sf => !dbIds.includes(sf.id) && !dbIds.includes(sf.slug))
    .map(sf => ({
      id: sf.slug || sf.id, title: sf.title, description: sf.description,
      long_description: sf.longDescription || '', duration: sf.duration, mode: sf.mode,
      tags: sf.tags || [], icon: sf.icon || 'code',
      is_free: sf.isFree ? 1 : 0, is_financed: sf.isFinanced ? 1 : 0,
      places_restantes: sf.places || 20, places_total: 20, actif: 1,
      overview_text: sf.overviewText || [], outcomes: sf.outcomes || [],
      curriculum: sf.curriculum || null, structure: null, careers_data: null,
      sessions_data: null, admission_criteria: null, faq: null, _staticOnly: true,
    }));
  const cmsAll = [...formations, ...staticOnly];

  const ef    = editCmsFormation;
  const setEf = setEditCmsFormation;
  const upd   = (patch) => setEf(prev => ({ ...prev, ...patch }));

  const TAB_LABELS   = ['1. Hero','2. Vue d\'ensemble','3. Compétences','4. Structure','5. Programme','6. Débouchés','7. Admission','8. FAQ'];
  const emptyFormation = { id:'', title:'', description:'', long_description:'', duration:'', mode:'', icon:'code', tags:[], is_free:0, is_financed:0, places_restantes:20, places_total:20, actif:1, overview_text:[], outcomes:[], curriculum:null, structure:null, careers_data:null, sessions_data:null, admission_criteria:null, faq:null, _isNew:true };

  return (
    <div>
      {ef ? (
        <div className="adm-card">
          <div className="adm-card__header">
            <button className="adm-back-btn" onClick={() => { setEf(null); setCmsFTab(0); }}>← Retour</button>
            <h2 className="adm-card__title">{ef._isNew ? 'Nouvelle formation' : ef.title}</h2>
            {ef._staticOnly && <span style={{ fontSize:'11px', background:'rgba(251,191,36,0.12)', color:'#f59e0b', border:'1px solid rgba(251,191,36,0.3)', borderRadius:'4px', padding:'2px 8px', marginLeft:'8px' }}>Statique — sera créée en DB à la sauvegarde</span>}
          </div>

          {/* Onglets navigation */}
          <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'24px', paddingBottom:'14px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
            {TAB_LABELS.map((label, i) => (
              <button key={i} type="button" onClick={() => setCmsFTab(i)}
                style={{ padding:'6px 13px', borderRadius:'6px', fontSize:'12px', fontWeight:500, border:'none', cursor:'pointer',
                  background: cmsFTab===i ? '#8BC53F' : 'rgba(255,255,255,0.07)',
                  color:      cmsFTab===i ? '#fff'    : 'rgba(255,255,255,0.55)' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Onglet 1 : Hero */}
          {cmsFTab === 0 && (
            <div className="adm-form-grid">
              {ef._isNew && (
                <div className="adm-field"><label className="adm-label">Identifiant (slug) <span className="adm-label-hint">* unique, lettres minuscules et tirets</span></label>
                  <input className="adm-input" value={ef.id||''} placeholder="ex: devops-fondamentaux"
                    onChange={e => upd({ id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-') })}/></div>
              )}
              <div className="adm-field"><label className="adm-label">Titre</label>
                <input className="adm-input" value={ef.title||''} onChange={e=>upd({title:e.target.value})}/></div>
              <div className="adm-field adm-field--full"><label className="adm-label">Description courte</label>
                <textarea className="adm-input" rows={2} value={ef.description||''} onChange={e=>upd({description:e.target.value})}/></div>
              <div className="adm-field"><label className="adm-label">Durée</label>
                <input className="adm-input" value={ef.duration||''} onChange={e=>upd({duration:e.target.value})} placeholder="12 semaines"/></div>
              <div className="adm-field"><label className="adm-label">Mode</label>
                <input className="adm-input" value={ef.mode||''} onChange={e=>upd({mode:e.target.value})} placeholder="Full-time / Part-time"/></div>
              <div className="adm-field"><label className="adm-label">Icône</label>
                <select className="adm-input" value={ef.icon||'code'} onChange={e=>upd({icon:e.target.value})}>
                  {['code','brain','chart','layout','zap'].map(v=><option key={v} value={v}>{v}</option>)}</select></div>
              <div className="adm-field adm-field--full"><label className="adm-label">Tags <span className="adm-label-hint">séparés par virgule</span></label>
                <input className="adm-input" value={Array.isArray(ef.tags)?ef.tags.join(', '):''}
                  onChange={e=>upd({tags:e.target.value.split(',').map(t=>t.trim()).filter(Boolean)})}/></div>
              <div className="adm-field"><label className="adm-label">Gratuite</label>
                <label className="adm-toggle"><input type="checkbox" checked={!!ef.is_free} onChange={e=>upd({is_free:e.target.checked?1:0})}/>
                  <span className="adm-toggle__track"/><span className="adm-toggle__label">{ef.is_free?'✓ Gratuite':'Payante'}</span></label></div>
              <div className="adm-field"><label className="adm-label">Financement disponible</label>
                <label className="adm-toggle"><input type="checkbox" checked={!!ef.is_financed} onChange={e=>upd({is_financed:e.target.checked?1:0})}/>
                  <span className="adm-toggle__track"/><span className="adm-toggle__label">{ef.is_financed?'✓ Oui':'Non'}</span></label></div>
              <div className="adm-field"><label className="adm-label">Places restantes</label>
                <input className="adm-input" type="number" value={ef.places_restantes??''} onChange={e=>upd({places_restantes:+e.target.value})}/></div>
              <div className="adm-field"><label className="adm-label">Statut</label>
                <label className="adm-toggle"><input type="checkbox" checked={!!ef.actif} onChange={e=>upd({actif:e.target.checked?1:0})}/>
                  <span className="adm-toggle__track"/><span className="adm-toggle__label">{ef.actif?'✓ Active':'Inactive'}</span></label></div>
            </div>
          )}

          {/* Onglet 2 : Vue d'ensemble */}
          {cmsFTab === 1 && (
            <div className="adm-form-grid">
              <div className="adm-field adm-field--full"><label className="adm-label">Description longue <span className="adm-label-hint">HTML basique accepté</span></label>
                <textarea className="adm-input" rows={4} value={ef.long_description||''} onChange={e=>upd({long_description:e.target.value})} placeholder="Décrivez la formation en détail…"/></div>
              <div className="adm-field adm-field--full"><label className="adm-label">Paragraphes <span className="adm-label-hint">section Vue d'ensemble</span></label>
                {(ef.overview_text||[]).map((para,i)=>(
                  <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'8px' }}>
                    <textarea className="adm-input" style={{ flex:1 }} rows={3} value={para}
                      onChange={e=>{const a=[...(ef.overview_text||[])];a[i]=e.target.value;upd({overview_text:a});}} placeholder={`Paragraphe ${i+1}`}/>
                    <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c', flexShrink:0, alignSelf:'flex-start', marginTop:'4px' }}
                      onClick={()=>upd({overview_text:(ef.overview_text||[]).filter((_,j)=>j!==i)})}><Trash2 size={14}/></button>
                  </div>
                ))}
                <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                  onClick={()=>upd({overview_text:[...(ef.overview_text||[]),'']})}>
                  <Plus size={12}/> Ajouter un paragraphe</button></div>
            </div>
          )}

          {/* Onglet 3 : Compétences */}
          {cmsFTab === 2 && (
            <div className="adm-form-grid">
              <div className="adm-field adm-field--full"><label className="adm-label">Compétences acquises <span className="adm-label-hint">« Ce que vous maîtriserez »</span></label>
                {(ef.outcomes||[]).map((o,i)=>(
                  <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
                    <input className="adm-input" style={{ flex:1 }} value={o} placeholder="ex: Maîtriser React et Node.js"
                      onChange={e=>{const a=[...(ef.outcomes||[])];a[i]=e.target.value;upd({outcomes:a});}}/>
                    <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c', flexShrink:0 }}
                      onClick={()=>upd({outcomes:(ef.outcomes||[]).filter((_,j)=>j!==i)})}><Trash2 size={14}/></button>
                  </div>
                ))}
                <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                  onClick={()=>upd({outcomes:[...(ef.outcomes||[]),'']})}>
                  <Plus size={12}/> Ajouter une compétence</button></div>
            </div>
          )}

          {/* Onglet 4 : Structure */}
          {cmsFTab === 3 && (
            <div>
              <p style={{ fontSize:'12px', color:'rgba(255,255,255,0.45)', marginBottom:'16px' }}>Phases/modules de la formation. Vide = structure par défaut.</p>
              {(ef.structure||[]).map((phase,pi)=>(
                <div key={pi} style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'16px', marginBottom:'12px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
                    <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)' }}>Phase {pi+1}</span>
                    <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c' }}
                      onClick={()=>upd({structure:(ef.structure||[]).filter((_,j)=>j!==pi)})}><Trash2 size={14}/></button>
                  </div>
                  <div className="adm-form-grid">
                    <div className="adm-field"><label className="adm-label">Numéro</label>
                      <input className="adm-input" value={phase.num||''} placeholder="Phase 01"
                        onChange={e=>{const a=[...(ef.structure||[])];a[pi]={...a[pi],num:e.target.value};upd({structure:a});}}/></div>
                    <div className="adm-field"><label className="adm-label">Titre</label>
                      <input className="adm-input" value={phase.title||''} placeholder="Bootcamp principal"
                        onChange={e=>{const a=[...(ef.structure||[])];a[pi]={...a[pi],title:e.target.value};upd({structure:a});}}/></div>
                    <div className="adm-field"><label className="adm-label">Durée</label>
                      <input className="adm-input" value={phase.duration||''} placeholder="12 semaines"
                        onChange={e=>{const a=[...(ef.structure||[])];a[pi]={...a[pi],duration:e.target.value};upd({structure:a});}}/></div>
                    <div className="adm-field adm-field--full"><label className="adm-label">Description</label>
                      <textarea className="adm-input" rows={2} value={phase.desc||''}
                        onChange={e=>{const a=[...(ef.structure||[])];a[pi]={...a[pi],desc:e.target.value};upd({structure:a});}}/></div>
                    <div className="adm-field adm-field--full"><label className="adm-label">Points clés</label>
                      {(phase.items||[]).map((it,ii)=>(
                        <div key={ii} style={{ display:'flex', gap:'6px', marginBottom:'5px' }}>
                          <input className="adm-input" style={{ flex:1, fontSize:'12px' }} value={it} placeholder="ex: 40h/sem (full-time)"
                            onChange={e=>{const a=[...(ef.structure||[])];const items=[...(a[pi].items||[])];items[ii]=e.target.value;a[pi]={...a[pi],items};upd({structure:a});}}/>
                          <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c', flexShrink:0 }}
                            onClick={()=>{const a=[...(ef.structure||[])];a[pi]={...a[pi],items:a[pi].items.filter((_,j)=>j!==ii)};upd({structure:a});}}><Trash2 size={12}/></button>
                        </div>
                      ))}
                      <button type="button" className="adm-btn-outline" style={{ fontSize:'11px', padding:'3px 10px', display:'flex', alignItems:'center', gap:'4px' }}
                        onClick={()=>{const a=[...(ef.structure||[])];a[pi]={...a[pi],items:[...(a[pi].items||[]),'']};upd({structure:a});}}>
                        <Plus size={10}/> Point</button></div>
                  </div>
                </div>
              ))}
              <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                onClick={()=>upd({structure:[...(ef.structure||[]),{num:'',title:'',duration:'',desc:'',items:[]}]})}>
                <Plus size={12}/> Ajouter une phase</button>
            </div>
          )}

          {/* Onglet 5 : Programme (Curriculum) */}
          {cmsFTab === 4 && (
            <div>
              {(ef.curriculum||[]).map((block,bi)=>(
                <div key={bi} style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'14px', marginBottom:'10px' }}>
                  <div style={{ display:'flex', gap:'8px', marginBottom:'8px' }}>
                    <input className="adm-input" style={{ flex:'0 0 150px' }} value={block.week||''} placeholder="Semaine 1–2"
                      onChange={e=>{const a=[...(ef.curriculum||[])];a[bi]={...a[bi],week:e.target.value};upd({curriculum:a});}}/>
                    <input className="adm-input" style={{ flex:1 }} value={block.title||''} placeholder="Titre du module"
                      onChange={e=>{const a=[...(ef.curriculum||[])];a[bi]={...a[bi],title:e.target.value};upd({curriculum:a});}}/>
                    <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c', flexShrink:0 }}
                      onClick={()=>upd({curriculum:(ef.curriculum||[]).filter((_,j)=>j!==bi)})}><Trash2 size={14}/></button>
                  </div>
                  <div style={{ paddingLeft:'8px' }}>
                    {(block.topics||[]).map((t,ti)=>(
                      <div key={ti} style={{ display:'flex', gap:'6px', marginBottom:'4px' }}>
                        <input className="adm-input" style={{ flex:1, fontSize:'12px', padding:'5px 10px' }} value={t} placeholder="Sujet / technologie"
                          onChange={e=>{const a=[...(ef.curriculum||[])];const topics=[...(a[bi].topics||[])];topics[ti]=e.target.value;a[bi]={...a[bi],topics};upd({curriculum:a});}}/>
                        <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c', flexShrink:0 }}
                          onClick={()=>{const a=[...(ef.curriculum||[])];a[bi]={...a[bi],topics:a[bi].topics.filter((_,j)=>j!==ti)};upd({curriculum:a});}}><Trash2 size={12}/></button>
                      </div>
                    ))}
                    <button type="button" className="adm-btn-outline" style={{ fontSize:'11px', padding:'3px 10px', marginTop:'4px', display:'flex', alignItems:'center', gap:'4px' }}
                      onClick={()=>{const a=[...(ef.curriculum||[])];a[bi]={...a[bi],topics:[...(a[bi].topics||[]),'']};upd({curriculum:a});}}>
                      <Plus size={10}/> Sujet</button>
                  </div>
                </div>
              ))}
              <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                onClick={()=>upd({curriculum:[...(ef.curriculum||[]),{week:'',title:'',topics:[]}]})}>
                <Plus size={12}/> Ajouter un module</button>
            </div>
          )}

          {/* Onglet 6 : Débouchés */}
          {cmsFTab === 5 && (
            <div>
              <p style={{ fontSize:'12px', color:'rgba(255,255,255,0.45)', marginBottom:'16px' }}>Métiers visés. Vide = données statiques existantes.</p>
              {(ef.careers_data||[]).map((c,ci)=>(
                <div key={ci} style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'16px', marginBottom:'12px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
                    <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)' }}>Métier {ci+1}</span>
                    <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c' }}
                      onClick={()=>upd({careers_data:(ef.careers_data||[]).filter((_,j)=>j!==ci)})}><Trash2 size={14}/></button>
                  </div>
                  <div className="adm-form-grid">
                    <div className="adm-field"><label className="adm-label">Poste</label>
                      <input className="adm-input" value={c.title||''} placeholder="Développeur Full-Stack"
                        onChange={e=>{const a=[...(ef.careers_data||[])];a[ci]={...a[ci],title:e.target.value};upd({careers_data:a});}}/></div>
                    <div className="adm-field"><label className="adm-label">Croissance</label>
                      <input className="adm-input" value={c.growth||''} placeholder="+45%"
                        onChange={e=>{const a=[...(ef.careers_data||[])];a[ci]={...a[ci],growth:e.target.value};upd({careers_data:a});}}/></div>
                    <div className="adm-field"><label className="adm-label">Salaire</label>
                      <input className="adm-input" value={c.salary||''} placeholder="800k – 2M FCFA / mois"
                        onChange={e=>{const a=[...(ef.careers_data||[])];a[ci]={...a[ci],salary:e.target.value};upd({careers_data:a});}}/></div>
                    <div className="adm-field"><label className="adm-label">Demande</label>
                      <input className="adm-input" value={c.demand||''} placeholder="Très élevée"
                        onChange={e=>{const a=[...(ef.careers_data||[])];a[ci]={...a[ci],demand:e.target.value};upd({careers_data:a});}}/></div>
                    <div className="adm-field"><label className="adm-label">Compétences <span className="adm-label-hint">virgule</span></label>
                      <input className="adm-input" value={Array.isArray(c.skills)?c.skills.join(', '):''}
                        onChange={e=>{const a=[...(ef.careers_data||[])];a[ci]={...a[ci],skills:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)};upd({careers_data:a});}}/></div>
                    <div className="adm-field"><label className="adm-label">Entreprises <span className="adm-label-hint">virgule</span></label>
                      <input className="adm-input" value={Array.isArray(c.companies)?c.companies.join(', '):''}
                        onChange={e=>{const a=[...(ef.careers_data||[])];a[ci]={...a[ci],companies:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)};upd({careers_data:a});}}/></div>
                  </div>
                </div>
              ))}
              <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                onClick={()=>upd({careers_data:[...(ef.careers_data||[]),{title:'',growth:'',salary:'',demand:'',skills:[],companies:[]}]})}>
                <Plus size={12}/> Ajouter un métier</button>
            </div>
          )}

          {/* Onglet 7 : Admission */}
          {cmsFTab === 6 && (
            <div className="adm-form-grid">
              <div className="adm-field adm-field--full"><label className="adm-label">Critères d'admission <span className="adm-label-hint">Vide = critères par défaut</span></label>
                {(ef.admission_criteria||[]).map((crit,i)=>(
                  <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
                    <input className="adm-input" style={{ flex:1 }} value={crit} placeholder="ex: Étudiants de toute filière"
                      onChange={e=>{const a=[...(ef.admission_criteria||[])];a[i]=e.target.value;upd({admission_criteria:a});}}/>
                    <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c', flexShrink:0 }}
                      onClick={()=>upd({admission_criteria:(ef.admission_criteria||[]).filter((_,j)=>j!==i)})}><Trash2 size={14}/></button>
                  </div>
                ))}
                <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                  onClick={()=>upd({admission_criteria:[...(ef.admission_criteria||[]),'']})}>
                  <Plus size={12}/> Ajouter un critère</button></div>
              <div className="adm-field adm-field--full"><label className="adm-label">Sessions <span className="adm-label-hint">Vide = pas de section sessions</span></label>
                {((ef.sessions_data?.items)||[]).map((s,si)=>(
                  <div key={si} style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'14px', marginBottom:'10px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
                      <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)' }}>Session {si+1}</span>
                      <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c' }}
                        onClick={()=>{const items=(ef.sessions_data?.items||[]).filter((_,j)=>j!==si);upd({sessions_data:{items}});}}><Trash2 size={14}/></button>
                    </div>
                    <div className="adm-form-grid">
                      <div className="adm-field"><label className="adm-label">Label</label>
                        <input className="adm-input" value={s.label||''} placeholder="Inscriptions ouvertes"
                          onChange={e=>{const items=[...(ef.sessions_data?.items||[])];items[si]={...items[si],label:e.target.value};upd({sessions_data:{items}});}}/></div>
                      <div className="adm-field"><label className="adm-label">Date</label>
                        <input className="adm-input" value={s.date||''} placeholder="Novembre 2026"
                          onChange={e=>{const items=[...(ef.sessions_data?.items||[])];items[si]={...items[si],date:e.target.value};upd({sessions_data:{items}});}}/></div>
                      <div className="adm-field"><label className="adm-label">Période</label>
                        <input className="adm-input" value={s.period||''} placeholder="3 Nov → 23 Jan"
                          onChange={e=>{const items=[...(ef.sessions_data?.items||[])];items[si]={...items[si],period:e.target.value};upd({sessions_data:{items}});}}/></div>
                      <div className="adm-field"><label className="adm-label">Lieu</label>
                        <input className="adm-input" value={s.location||''} placeholder="Abidjan (présentiel + en ligne)"
                          onChange={e=>{const items=[...(ef.sessions_data?.items||[])];items[si]={...items[si],location:e.target.value};upd({sessions_data:{items}});}}/></div>
                      <div className="adm-field"><label className="adm-label">Places</label>
                        <input className="adm-input" type="number" value={s.spots||''}
                          onChange={e=>{const items=[...(ef.sessions_data?.items||[])];items[si]={...items[si],spots:+e.target.value};upd({sessions_data:{items}});}}/></div>
                      <div className="adm-field"><label className="adm-label">Mise en avant</label>
                        <label className="adm-toggle"><input type="checkbox" checked={!!s.featured}
                          onChange={e=>{const items=[...(ef.sessions_data?.items||[])];items[si]={...items[si],featured:e.target.checked};upd({sessions_data:{items}});}}/>
                          <span className="adm-toggle__track"/><span className="adm-toggle__label">{s.featured?'✓ Principale':'Secondaire'}</span></label></div>
                      <div className="adm-field"><label className="adm-label">{s.featured?'Date limite':'Date d\'ouverture'}</label>
                        <input className="adm-input" value={s.featured?(s.deadline||''):(s.openDate||'')} placeholder={s.featured?'15 Oct 2026':'Novembre 2026'}
                          onChange={e=>{const items=[...(ef.sessions_data?.items||[])];items[si]={...items[si],[s.featured?'deadline':'openDate']:e.target.value};upd({sessions_data:{items}});}}/></div>
                    </div>
                  </div>
                ))}
                <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                  onClick={()=>{const items=[...(ef.sessions_data?.items||[]),{id:`s-${Date.now()}`,label:'',date:'',period:'',spots:25,location:'Abidjan',featured:false}];upd({sessions_data:{items}});}}>
                  <Plus size={12}/> Ajouter une session</button></div>
            </div>
          )}

          {/* Onglet 8 : FAQ */}
          {cmsFTab === 7 && (
            <div>
              <p style={{ fontSize:'12px', color:'rgba(255,255,255,0.45)', marginBottom:'16px' }}>Vide = FAQ commune à toutes les formations.</p>
              {(ef.faq||[]).map((item,fi)=>(
                <div key={fi} style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'14px', marginBottom:'10px' }}>
                  <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'8px' }}>
                    <button type="button" className="adm-btn-icon" style={{ color:'#e05c5c' }}
                      onClick={()=>upd({faq:(ef.faq||[]).filter((_,j)=>j!==fi)})}><Trash2 size={14}/></button>
                  </div>
                  <div className="adm-field" style={{ marginBottom:'8px' }}><label className="adm-label">Question</label>
                    <input className="adm-input" value={item.q||''}
                      onChange={e=>{const a=[...(ef.faq||[])];a[fi]={...a[fi],q:e.target.value};upd({faq:a});}}/></div>
                  <div className="adm-field"><label className="adm-label">Réponse</label>
                    <textarea className="adm-input" rows={3} value={item.a||''}
                      onChange={e=>{const a=[...(ef.faq||[])];a[fi]={...a[fi],a:e.target.value};upd({faq:a});}}/></div>
                </div>
              ))}
              <button type="button" className="adm-btn-outline" style={{ fontSize:'12px', padding:'5px 12px', display:'flex', alignItems:'center', gap:'5px' }}
                onClick={()=>upd({faq:[...(ef.faq||[]),{q:'',a:''}]})}>
                <Plus size={12}/> Ajouter une question</button>
            </div>
          )}

          <div className="adm-card__actions" style={{ marginTop:'28px' }}>
            <button className="adm-btn-primary" onClick={()=>saveCmsFormation(ef)}>Enregistrer</button>
            <button className="adm-btn-outline" onClick={()=>{ setEf(null); setCmsFTab(0); }}>Annuler</button>
            {!ef._isNew && (
              <a href={`/formations/${ef.id}`} target="_blank" rel="noreferrer"
                style={{ marginLeft:'auto', fontSize:'12px', color:'rgba(255,255,255,0.45)', display:'flex', alignItems:'center', gap:'5px' }}>
                <ExternalLink size={13}/> Voir la page
              </a>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="adm-section-header">
            <h2 className="adm-section-title">CMS Formations <span className="adm-section-sub">— pages détaillées</span></h2>
            <button className="adm-btn-primary" style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'13px', padding:'8px 16px' }}
              onClick={()=>{ setEf({...emptyFormation}); setCmsFTab(0); }}>
              <Plus size={15}/> Nouvelle formation
            </button>
          </div>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr><th>Formation</th><th>Slug</th><th>Type</th><th>Statut</th><th>Action</th></tr></thead>
              <tbody>
                {cmsAll.map(f=>(
                  <tr key={f.id}>
                    <td>
                      <div className="adm-cell-primary">{f.title}</div>
                      <div className="adm-cell-tags">{(f.tags||[]).slice(0,3).join(' · ')}</div>
                    </td>
                    <td><code style={{ fontSize:'11px', color:'rgba(255,255,255,0.45)' }}>/formations/{f.id}</code></td>
                    <td>{f._staticOnly
                      ? <span className="adm-badge" style={{ color:'#f59e0b', background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.3)' }}>Statique</span>
                      : <span className="adm-badge" style={{ color:'#8BC53F', background:'rgba(139,197,63,0.1)', border:'1px solid rgba(139,197,63,0.3)' }}>Base de données</span>}</td>
                    <td>{f._staticOnly
                      ? <span style={{ fontSize:'12px', color:'rgba(255,255,255,0.3)' }}>Non synchronisée</span>
                      : <span style={{ fontSize:'12px', color:'#8BC53F' }}>✓ Synchronisée</span>}</td>
                    <td>
                      <button className="adm-btn-icon" title="Modifier le contenu de la page"
                        onClick={()=>{ setEf({...f, overview_text:f.overview_text||[], outcomes:f.outcomes||[], curriculum:f.curriculum||null, structure:f.structure||null, careers_data:f.careers_data||null, sessions_data:f.sessions_data||null, admission_criteria:f.admission_criteria||null, faq:f.faq||null }); setCmsFTab(0); }}>
                        <Pencil size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Admin principal ────────────────────────────────────────────── */

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem('tta_admin_token'));
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tta_admin_info')); } catch { return null; }
  });
  const [section, setSection]       = useState('Tableau de bord');
  const [toast, setToast]           = useState(null);
  const [loading, setLoading]       = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Data states
  const [stats, setStats]               = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [contacts, setContacts]         = useState([]);
  const [formations, setFormations]     = useState([]);
  const [projets, setProjets]           = useState([]);
  const [partenaires, setPartenaires]   = useState([]);
  const [articles, setArticles]         = useState([]);
  const [settings, setSettings]         = useState({});
  const [editSettings, setEditSettings] = useState({});

  // UI states
  const [filterStatut, setFilterStatut]         = useState('toutes');
  const [filterContactType, setFilterContactType] = useState('tous');
  const [viewContact, setViewContact]             = useState(null);
  const [viewCandidature, setViewCandidature]     = useState(null);
  const [editFormation, setEditFormation]       = useState(null);
  const [editCmsFormation, setEditCmsFormation] = useState(null); // CMS > Formations
  const [editArticle, setEditArticle]           = useState(null); // null=liste, {}=nouveau, {...}=édition
  const [editProjet, setEditProjet]       = useState(null);
  const [editPartenaire, setEditPartenaire] = useState(null);

  // Médiathèque
  const [uploads, setUploads]         = useState([]);
  const [mediaOpen, setMediaOpen]     = useState(false);
  const [mediaCb, setMediaCb]         = useState(null);

  // Utilisateurs & Journal
  const [users, setUsers]         = useState([]);
  const [auditLog, setAuditLog]   = useState([]);
  const [editUser, setEditUser]   = useState(null); // null=liste, {}=nouveau, {...}=édition

  // CMS — Pages
  const [pages, setPages]                   = useState([]);
  const [currentPageId, setCurrentPageId]   = useState(null);
  const [pageSections, setPageSections]     = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [addSectionModal, setAddSectionModal] = useState(false);

  // CMS — Navigation
  const [navItems, setNavItems]         = useState([]); // arbre {id, label, href, actif, children[]}
  const [addNavForm, setAddNavForm]     = useState(null); // null | { parentId: null|id }

  // CMS — Footer
  const [footerSections, setFooterSections]   = useState([]);
  const [expandedFooter, setExpandedFooter]   = useState(null);

  // DnD sensors
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const openMediaPicker = (cb) => {
    setMediaCb(() => cb);
    setMediaOpen(true);
  };

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3200);
  };

  const handleLogin  = (tok, adminInfo) => { setToken(tok); setAdmin(adminInfo); };
  const handleLogout = () => {
    localStorage.removeItem('tta_admin_token');
    localStorage.removeItem('tta_admin_info');
    setToken(null); setAdmin(null);
  };

  const loadSection = useCallback(async (sec) => {
    if (!token) return;
    setLoading(true);
    try {
      if (sec === 'Tableau de bord') {
        setStats(await api.getStats(token));
      } else if (sec === 'Candidatures') {
        const params = filterStatut !== 'toutes' ? { statut: filterStatut } : {};
        setCandidatures((await api.getCandidatures(token, params)).data || []);
      } else if (sec === 'Projets') {
        setProjets((await api.getProjets(token)).data || []);
      } else if (sec === 'Partenaires') {
        setPartenaires((await api.getPartenaires(token)).data || []);
      } else if (sec === 'Actualités') {
        setArticles((await api.getArticles(token)).data || []);
      } else if (sec === 'Pages') {
        setCurrentPageId(null);
        setPages((await api.getPages(token)).data || []);
      } else if (sec === 'Navigation') {
        setNavItems((await api.getNavigation(token)).data || []);
        setAddNavForm(null);
      } else if (sec === 'Footer') {
        const rows = (await api.getFooter(token)).data || [];
        setFooterSections(rows.map(r => ({ ...r, content: r.content || {} })));
        setExpandedFooter(null);
      } else if (sec === 'Médiathèque') {
        setUploads((await api.getUploads(token)).data || []);
      } else if (sec === 'Utilisateurs') {
        setUsers((await api.getUsers(token)).data || []);
      } else if (sec === 'Journal') {
        setAuditLog((await api.getAuditLog(token)).data || []);
      } else if (sec === 'Contacts') {
        const params = filterContactType !== 'tous' ? { type: filterContactType } : {};
        setContacts((await api.getContacts(token, params)).data || []);
      } else if (sec === 'Paramètres' || sec === 'Compteur FORPRODE') {
        const d = (await api.getSettings(token)).data || {};
        setSettings(d); setEditSettings(d);
      }
    } catch (err) {
      if (err.message.includes('401') || err.message.includes('Token')) handleLogout();
      else showToast('Erreur : ' + err.message, true);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatut, filterContactType]);

  useEffect(() => { loadSection(section); }, [section]);
  useEffect(() => { if (section === 'Candidatures') loadSection('Candidatures'); }, [filterStatut]);
  useEffect(() => { if (section === 'Contacts') loadSection('Contacts'); }, [filterContactType]);

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  // ── Actions ──
  const updateStatut = async (id, statut) => {
    try {
      await api.updateCandidature(id, { statut }, token);
      setCandidatures(p => p.map(c => c.id === id ? { ...c, statut } : c));
      showToast('Statut mis à jour ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const deleteContactMsg = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await api.deleteContact(id, token);
      setContacts(p => p.filter(c => c.id !== id));
      showToast('Message supprimé ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const saveFormation = async (f) => {
    try {
      if (f._isNew) {
        await api.createFormation(f, token);
        setFormations(p => [...p, { ...f, _isNew: undefined }]);
        showToast('Formation créée ✓');
      } else {
        await api.updateFormation(f.id, f, token);
        setFormations(p => p.map(x => x.id === f.id ? f : x));
        showToast('Formation mise à jour ✓');
      }
      setEditFormation(null);
    } catch (err) { showToast(err.message, true); }
  };

  const saveCmsFormation = async (f) => {
    const { _staticOnly, ...data } = f;
    const dbEntry = formations.find(x => x.id === f.id);
    try {
      if (!dbEntry) {
        // Formation statique sans entrée DB : on la crée
        await api.createFormation(data, token);
        const res = await api.getFormations(token);
        setFormations(res.data || []);
      } else {
        await api.updateFormation(f.id, data, token);
        setFormations(p => p.map(x => x.id === f.id ? { ...x, ...data } : x));
      }
      showToast('Formation mise à jour ✓');
      setEditCmsFormation({ ...data, _staticOnly: false });
    } catch (err) { showToast(err.message, true); }
  };

  const deleteFormation = async (id) => {
    if (!window.confirm('Supprimer cette formation ? Cette action est irréversible.')) return;
    try {
      await api.deleteFormation(id, token);
      setFormations(p => p.filter(f => f.id !== id));
      showToast('Formation supprimée ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const saveCountdown = async () => {
    try {
      await api.saveSettings({
        forprode_date: editSettings.forprode_date,
        forprode_mode: editSettings.forprode_mode,
      }, token);
      setSettings(p => ({ ...p, ...editSettings }));
      showToast('Compteur mis à jour ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const saveSettingsForm = async () => {
    try {
      await api.saveSettings(editSettings, token);
      setSettings(editSettings);
      showToast('Paramètres enregistrés ✓');
    } catch (err) { showToast(err.message, true); }
  };

  // ── Articles ──
  const saveArticle = async (data) => {
    try {
      if (data.id) {
        await api.updateArticle(data.id, data, token);
        setArticles(p => p.map(a => a.id === data.id ? { ...a, ...data } : a));
        showToast('Article mis à jour ✓');
      } else {
        const res = await api.createArticle(data, token);
        setArticles(p => [res.data ?? res, ...p]);
        showToast('Article créé ✓');
      }
      setEditArticle(null);
    } catch (err) { showToast(err.message, true); }
  };

  const removeArticle = async (id) => {
    if (!window.confirm('Supprimer cet article définitivement ?')) return;
    try {
      await api.deleteArticle(id, token);
      setArticles(p => p.filter(a => a.id !== id));
      showToast('Article supprimé');
    } catch (err) { showToast(err.message, true); }
  };

  // ── Partenaires ──
  const savePartenaire = async (data) => {
    try {
      if (data.id) {
        await api.updatePartenaire(data.id, data, token);
        setPartenaires(p => p.map(x => x.id === data.id ? { ...x, ...data } : x));
        showToast('Partenaire mis à jour ✓');
      } else {
        const res = await api.createPartenaire(data, token);
        setPartenaires(p => [...p, res.data ?? res]);
        showToast('Partenaire ajouté ✓');
      }
      setEditPartenaire(null);
    } catch (err) { showToast(err.message, true); }
  };

  const removePartenaire = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await api.deletePartenaire(id, token);
      setPartenaires(p => p.filter(x => x.id !== id));
      showToast('Partenaire supprimé');
    } catch (err) { showToast(err.message, true); }
  };

  // ── Utilisateurs ──
  const saveUser = async (data) => {
    try {
      if (data.id) {
        await api.updateUser(data.id, data, token);
        setUsers(p => p.map(u => u.id === data.id ? { ...u, ...data } : u));
        showToast('Utilisateur mis à jour ✓');
      } else {
        const res = await api.createUser(data, token);
        setUsers(p => [{ ...data, id: res.id, actif: 1 }, ...p]);
        showToast('Utilisateur créé ✓');
      }
      setEditUser(null);
    } catch (err) { showToast(err.message, true); }
  };

  const toggleUserActif = async (u) => {
    try {
      await api.updateUser(u.id, { actif: u.actif ? 0 : 1 }, token);
      setUsers(p => p.map(x => x.id === u.id ? { ...x, actif: x.actif ? 0 : 1 } : x));
      showToast(u.actif ? 'Compte désactivé' : 'Compte activé ✓');
    } catch (err) { showToast(err.message, true); }
  };

  // ── CMS Navigation ──
  const saveNavItem = async (id, data) => {
    try {
      await api.updateNavItem(id, data, token);
      setNavItems(prev => updateNavTree(prev, id, data));
      showToast('Item mis à jour ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const createNavItemAction = async ({ label, href, parentId }) => {
    if (!label.trim()) return;
    try {
      const res = await api.createNavItem({ label, href: href || '#', parent_id: parentId ?? null }, token);
      // Recharger pour avoir la nouvelle arborescence
      const fresh = await api.getNavigation(token);
      setNavItems(fresh.data || []);
      setAddNavForm(null);
      showToast('Item ajouté ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const deleteNavItemAction = async (id) => {
    if (!window.confirm('Supprimer cet item (et ses sous-menus) ?')) return;
    try {
      await api.deleteNavItem(id, token);
      const fresh = await api.getNavigation(token);
      setNavItems(fresh.data || []);
      showToast('Item supprimé');
    } catch (err) { showToast(err.message, true); }
  };

  const moveNavItem = async (items, idx, dir) => {
    const reordered = arrayMove(items, idx, idx + dir);
    const order = reordered.map((it, i) => ({ id: it.id, position: i + 1 }));
    try {
      await api.reorderNavigation(order, token);
      // Recharger pour refléter le nouvel ordre
      const fresh = await api.getNavigation(token);
      setNavItems(fresh.data || []);
    } catch (err) { showToast(err.message, true); }
  };

  // Helper : mettre à jour un nœud dans l'arbre nav (actif, label, href)
  const updateNavTree = (tree, id, data) =>
    tree.map(node => {
      if (node.id === id) return { ...node, ...data };
      if (node.children?.length) return { ...node, children: updateNavTree(node.children, id, data) };
      return node;
    });

  // ── CMS Footer ──
  const saveFooterSection = async (id, content) => {
    try {
      await api.updateFooterSection(id, { content }, token);
      setFooterSections(p => p.map(s => s.id === id ? { ...s, content } : s));
      showToast('Section footer enregistrée ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const toggleFooterActif = async (sec) => {
    try {
      await api.updateFooterSection(sec.id, { actif: sec.actif ? 0 : 1 }, token);
      setFooterSections(p => p.map(s => s.id === sec.id ? { ...s, actif: sec.actif ? 0 : 1 } : s));
    } catch (err) { showToast(err.message, true); }
  };

  const moveFooterSection = async (idx, dir) => {
    const reordered = arrayMove(footerSections, idx, idx + dir);
    setFooterSections(reordered);
    const order = reordered.map((s, i) => ({ id: s.id, position: i + 1 }));
    try { await api.reorderFooter(order, token); }
    catch (err) { showToast(err.message, true); }
  };

  const deleteFooterSection = async (id) => {
    if (!window.confirm('Supprimer cette section du footer ?')) return;
    try {
      await api.deleteFooterSection(id, token);
      setFooterSections(p => p.filter(s => s.id !== id));
      showToast('Section supprimée');
    } catch (err) { showToast(err.message, true); }
  };

  const addFooterSection = async (type) => {
    const defaults = {
      brand:      { name: 'Tech Talent Accelerator', tagline: '' },
      links:      { title: 'Nouvelle colonne', items: [] },
      contact:    { email: '', phone: '', address: '' },
      social:     { items: [] },
      legal:      { copyright: `© ${new Date().getFullYear()} Tech Talent Accelerator. Tous droits réservés.` },
      newsletter: { title: '', text: '' },
    };
    const content = defaults[type] || {};
    try {
      const res = await api.createFooterSection({ type, content }, token);
      setFooterSections(p => [...p, { id: res.id, type, content, position: p.length + 1, actif: 1 }]);
      setExpandedFooter(res.id);
      showToast('Section ajoutée ✓');
    } catch (err) { showToast(err.message, true); }
  };

  // ── CMS Pages ──
  const loadPageSections = async (pageId) => {
    try {
      const res = await api.getPageSections(pageId, token);
      setPageSections(res.data || []);
      setExpandedSection(null);
    } catch (err) { showToast(err.message, true); }
  };

  const saveSectionContent = async (sectionId, content) => {
    try {
      await api.updateSection(sectionId, { content }, token);
      setPageSections(p => p.map(s => s.id === sectionId ? { ...s, content } : s));
      showToast('Section enregistrée ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const toggleSectionActif = async (sec) => {
    try {
      await api.updateSection(sec.id, { actif: sec.actif ? 0 : 1 }, token);
      setPageSections(p => p.map(s => s.id === sec.id ? { ...s, actif: sec.actif ? 0 : 1 } : s));
    } catch (err) { showToast(err.message, true); }
  };

  const removeSectionFromPage = async (id) => {
    if (!window.confirm('Supprimer cette section ?')) return;
    try {
      await api.deleteSection(id, token);
      setPageSections(p => p.filter(s => s.id !== id));
      showToast('Section supprimée');
    } catch (err) { showToast(err.message, true); }
  };

  const addSectionToPage = async (type) => {
    const label = SECTION_LABELS[type] || type;
    try {
      const res = await api.addPageSection(currentPageId, { type, label, content: {} }, token);
      setPageSections(p => [...p, { id: res.id, type, label, content: {}, position: p.length + 1, actif: 1 }]);
      setAddSectionModal(false);
      setExpandedSection(res.id);
      showToast('Section ajoutée ✓');
    } catch (err) { showToast(err.message, true); }
  };

  const moveSectionDir = async (idx, dir) => {
    const newSections = arrayMove(pageSections, idx, idx + dir);
    setPageSections(newSections);
    const order = newSections.map((s, i) => ({ id: s.id, position: i + 1 }));
    try {
      await api.reorderPageSections(currentPageId, order, token);
    } catch (err) { showToast(err.message, true); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = pageSections.findIndex(s => s.id === active.id);
    const newIdx = pageSections.findIndex(s => s.id === over.id);
    const newSections = arrayMove(pageSections, oldIdx, newIdx);
    setPageSections(newSections);
    const order = newSections.map((s, i) => ({ id: s.id, position: i + 1 }));
    try {
      await api.reorderPageSections(currentPageId, order, token);
    } catch (err) { showToast(err.message, true); }
  };

  // ── Projets ──
  const saveProjet = async (data) => {
    try {
      if (data.id) {
        // Modification
        await api.updateProjet(data.id, data, token);
        setProjets(p => p.map(x => x.id === data.id ? { ...x, ...data } : x));
        showToast('Projet mis à jour ✓');
      } else {
        // Création
        const res = await api.createProjet(data, token);
        setProjets(p => [...p, res.data]);
        showToast('Projet créé ✓');
      }
      setEditProjet(null);
    } catch (err) { showToast(err.message, true); }
  };

  const joursForprode = () => {
    const d = editSettings.forprode_date || settings.forprode_date;
    if (!d) return 0;
    return Math.max(0, Math.floor((new Date(d) - new Date()) / 86400000));
  };

  return (
    <div className="adm-wrap">

      {/* Toast */}
      {toast && (
        <div className={`adm-toast ${toast.isError ? 'adm-toast--error' : ''}`}>
          {toast.isError ? <XCircle size={14} /> : <CheckCircle2 size={14} />}
          {toast.msg}
        </div>
      )}

      {/* MediaPicker modal */}
      <MediaPicker
        open={mediaOpen}
        token={token}
        onSelect={url => { mediaCb && mediaCb(url); }}
        onClose={() => setMediaOpen(false)}
      />

      <Sidebar
        section={section} setSection={setSection}
        admin={admin} onLogout={handleLogout}
        mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}
      />

      <div className="adm-main">
        <Topbar section={section} admin={admin} onMenu={() => setSidebarOpen(true)} />

        <div className="adm-content">

          {loading && (
            <div className="adm-loader">
              <div className="adm-loader__spinner" />
              <span>Chargement…</span>
            </div>
          )}

          {/* ── TABLEAU DE BORD ── */}
          {!loading && section === 'Tableau de bord' && stats && (
            <div>
              {/* KPIs */}
              <div className="adm-kpi-grid">
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#4FA64618', color: '#4FA646' }}><Users size={20} /></div>
                  <div className="adm-kpi-val">{stats.candidatures?.total ?? '–'}</div>
                  <div className="adm-kpi-label">Candidatures reçues</div>
                  <div className="adm-kpi-delta adm-kpi-delta--up"><TrendingUp size={11} /> +{stats.candidatures?.semaine ?? 0} cette semaine</div>
                </div>
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#f59e0b18', color: '#f59e0b' }}><Clock size={20} /></div>
                  <div className="adm-kpi-val">{stats.candidatures?.attente ?? '–'}</div>
                  <div className="adm-kpi-label">En attente de réponse</div>
                  <div className="adm-kpi-delta adm-kpi-delta--warn">À traiter en priorité</div>
                </div>
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#3b82f618', color: '#3b82f6' }}><GraduationCap size={20} /></div>
                  <div className="adm-kpi-val">{stats.formations_actives ?? '–'}</div>
                  <div className="adm-kpi-label">Formations actives</div>
                  <div className="adm-kpi-delta">Programmes certifiants</div>
                </div>
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#ec489918', color: '#ec4899' }}><Newspaper size={20} /></div>
                  <div className="adm-kpi-val">{stats.articles_publies ?? '–'}</div>
                  <div className="adm-kpi-label">Articles publiés</div>
                  <div className="adm-kpi-delta">Blog & Actualités</div>
                </div>
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#14b8a618', color: '#14b8a6' }}><Handshake size={20} /></div>
                  <div className="adm-kpi-val">{stats.partenaires_actifs ?? '–'}</div>
                  <div className="adm-kpi-label">Partenaires actifs</div>
                  <div className="adm-kpi-delta">Logos sur le site</div>
                </div>
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#f9731618', color: '#f97316' }}><FolderKanban size={20} /></div>
                  <div className="adm-kpi-val">{stats.projets_en_cours ?? '–'}</div>
                  <div className="adm-kpi-label">Projets en cours</div>
                  <div className="adm-kpi-delta">Programmes actifs</div>
                </div>
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#8b5cf618', color: '#8b5cf6' }}><Timer size={20} /></div>
                  <div className="adm-kpi-val">{stats.forprode?.jours_restants ?? '–'}</div>
                  <div className="adm-kpi-label">Jours avant échéance</div>
                  <div className="adm-kpi-delta">FORPRODE 40 IA</div>
                </div>
                <div className="adm-kpi-card">
                  <div className="adm-kpi-icon" style={{ background: '#06b6d418', color: '#06b6d4' }}><MessageSquare size={20} /></div>
                  <div className="adm-kpi-val">{stats.contacts?.total ?? '–'}</div>
                  <div className="adm-kpi-label">Messages reçus</div>
                  <div className="adm-kpi-delta adm-kpi-delta--up"><TrendingUp size={11} /> +{stats.contacts?.semaine ?? 0} cette semaine</div>
                </div>
              </div>

              {/* Raccourcis rapides */}
              <div className="adm-dash-shortcuts">
                <button className="adm-shortcut-btn" onClick={() => setSection('Candidatures')}>
                  <Users size={15} /> Voir les candidatures
                </button>
                <button className="adm-shortcut-btn" onClick={() => setSection('Actualités')}>
                  <Newspaper size={15} /> Nouvel article
                </button>
                <button className="adm-shortcut-btn" onClick={() => setSection('Pages')}>
                  <LayoutTemplate size={15} /> Éditer les pages
                </button>
                <button className="adm-shortcut-btn" onClick={() => setSection('Partenaires')}>
                  <Handshake size={15} /> Gérer les partenaires
                </button>
                <button className="adm-shortcut-btn" onClick={() => setSection('Contacts')}>
                  <MessageSquare size={15} /> Messages contact
                </button>
              </div>

              {/* Répartition candidatures 30j */}
              {(stats.repartition || []).length > 0 && (
                <div className="adm-dash-row">
                  <div className="adm-dash-panel">
                    <div className="adm-section-header"><h2 className="adm-section-title">Répartition candidatures (30 jours)</h2></div>
                    <div className="adm-repartition">
                      {(stats.repartition || []).map(r => {
                        const total30 = (stats.repartition || []).reduce((s, x) => s + +x.nb, 0);
                        const pct = total30 ? Math.round((+r.nb / total30) * 100) : 0;
                        const colors = { en_attente: '#f59e0b', entretien: '#3b82f6', accepte: '#22c55e', refuse: '#ef4444' };
                        const labels = { en_attente: 'En attente', entretien: 'Entretien', accepte: 'Accepté', refuse: 'Refusé' };
                        return (
                          <div key={r.statut} className="adm-repartition__row">
                            <span className="adm-repartition__label">{labels[r.statut] || r.statut}</span>
                            <div className="adm-repartition__bar-wrap">
                              <div className="adm-repartition__bar" style={{ width: `${pct}%`, background: colors[r.statut] || '#888' }} />
                            </div>
                            <span className="adm-repartition__nb">{r.nb} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tendance 30j */}
                  {(stats.tendance || []).length > 0 && (
                    <div className="adm-dash-panel">
                      <div className="adm-section-header"><h2 className="adm-section-title">Tendance (30 jours)</h2></div>
                      <div className="adm-tendance">
                        {(() => {
                          const max = Math.max(...stats.tendance.map(t => +t.nb), 1);
                          return stats.tendance.map(t => (
                            <div key={t.jour} className="adm-tendance__col" title={`${t.jour} : ${t.nb}`}>
                              <div className="adm-tendance__bar" style={{ height: `${Math.round((+t.nb / max) * 56)}px` }} />
                              <span className="adm-tendance__day">{t.jour?.slice(8)}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dernières candidatures */}
              <div className="adm-section-header" style={{ marginTop: '24px' }}>
                <h2 className="adm-section-title">Dernières candidatures</h2>
                <button className="adm-btn-sm" onClick={() => setSection('Candidatures')}>Voir tout →</button>
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr><th>Candidat</th><th>Formation</th><th>Statut</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {(stats.dernieres_candidatures || []).map(c => (
                      <tr key={c.id}>
                        <td>
                          <div className="adm-cell-primary">{c.prenom} {c.nom}</div>
                          <div className="adm-cell-sub">{c.email}</div>
                        </td>
                        <td className="adm-cell-muted">{formations.find(f => f.id === c.formation_id)?.title || c.formation_id}</td>
                        <td><Badge statut={c.statut} /></td>
                        <td className="adm-cell-muted">{c.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                    {!stats.dernieres_candidatures?.length && (
                      <tr><td colSpan={4}>
                        <Empty icon={<Users size={28} />} title="Aucune candidature" />
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── FORMATIONS ── */}
          {!loading && section === 'Formations' && (
            <div>
              {editFormation ? (
                <div className="adm-card">
                  <div className="adm-card__header">
                    <button className="adm-back-btn" onClick={() => setEditFormation(null)}>← Retour</button>
                    <h2 className="adm-card__title">{editFormation._isNew ? 'Nouvelle formation' : `Modifier : ${editFormation.title}`}</h2>
                  </div>
                  <div className="adm-form-grid">
                    <div className="adm-field adm-field--full">
                      <div className="adm-field-hint" style={{ marginBottom: 0 }}>
                        {editFormation._isNew
                          ? "Remplissez les informations de la nouvelle formation. L'identifiant doit être unique (ex: full-stack-ai) et ne peut plus être modifié après création."
                          : "Les champs ci-dessous sont synchronisés en temps réel sur le site. Modifiez librement titre, durée, description et places."}
                      </div>
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Identifiant (slug) {editFormation._isNew ? <span className="adm-label-hint">* lettres minuscules et tirets</span> : <span className="adm-label-hint">(non modifiable)</span>}</label>
                      <input
                        className="adm-input"
                        value={editFormation.id || ''}
                        readOnly={!editFormation._isNew}
                        style={editFormation._isNew ? {} : { opacity: 0.5, cursor: 'default' }}
                        onChange={e => editFormation._isNew && setEditFormation({ ...editFormation, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                        placeholder="ex: full-stack-ai"
                      />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Titre</label>
                      <input className="adm-input" value={editFormation.title || ''} onChange={e => setEditFormation({ ...editFormation, title: e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Durée</label>
                      <input className="adm-input" value={editFormation.duration || ''} onChange={e => setEditFormation({ ...editFormation, duration: e.target.value })} />
                    </div>
                    <div className="adm-field adm-field--full">
                      <label className="adm-label">Description</label>
                      <textarea className="adm-input" rows={3} value={editFormation.description || ''} onChange={e => setEditFormation({ ...editFormation, description: e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Mode</label>
                      <input className="adm-input" value={editFormation.mode || ''} onChange={e => setEditFormation({ ...editFormation, mode: e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Places restantes</label>
                      <input className="adm-input" type="number" value={editFormation.places_restantes ?? ''} onChange={e => setEditFormation({ ...editFormation, places_restantes: +e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Places totales</label>
                      <input className="adm-input" type="number" value={editFormation.places_total ?? ''} onChange={e => setEditFormation({ ...editFormation, places_total: +e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Tags (séparés par virgule)</label>
                      <input
                        className="adm-input"
                        value={Array.isArray(editFormation.tags) ? editFormation.tags.join(', ') : ''}
                        onChange={e => setEditFormation({ ...editFormation, tags: e.target.value.split(',').map(t => t.trim()) })}
                      />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Formation gratuite</label>
                      <label className="adm-toggle">
                        <input type="checkbox" checked={!!editFormation.is_free} onChange={e => setEditFormation({ ...editFormation, is_free: e.target.checked ? 1 : 0 })} />
                        <span className="adm-toggle__track" />
                        <span className="adm-toggle__label">{editFormation.is_free ? '✓ Gratuite' : 'Payante'}</span>
                      </label>
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Statut</label>
                      <label className="adm-toggle">
                        <input type="checkbox" checked={!!editFormation.actif} onChange={e => setEditFormation({ ...editFormation, actif: e.target.checked ? 1 : 0 })} />
                        <span className="adm-toggle__track" />
                        <span className="adm-toggle__label">{editFormation.actif ? '✓ Active' : 'Inactive'}</span>
                      </label>
                    </div>
                  </div>

                  <div className="adm-card__actions">
                    <button className="adm-btn-primary" onClick={() => saveFormation(editFormation)}>Enregistrer</button>
                    <button className="adm-btn-outline" onClick={() => setEditFormation(null)}>Annuler</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="adm-section-header">
                    <h2 className="adm-section-title">Cards Formations <span className="adm-section-sub">— liste & homepage</span></h2>
                    <button className="adm-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '8px 16px' }}
                      onClick={() => setEditFormation({ id: '', title: '', description: '', duration: '', mode: '', tags: [], is_free: 0, places_total: 20, places_restantes: 20, ordre: 0, actif: 1, _isNew: true })}>
                      <Plus size={15} /> Nouvelle formation
                    </button>
                  </div>
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr><th>Formation</th><th>Mode</th><th>Durée</th><th>Places</th><th>Gratuit</th><th>Action</th></tr>
                      </thead>
                      <tbody>
                        {formations.map(f => (
                          <tr key={f.id}>
                            <td>
                              <div className="adm-cell-primary">{f.title}</div>
                              <div className="adm-cell-tags">{(Array.isArray(f.tags) ? f.tags : []).slice(0, 3).join(' · ')}</div>
                            </td>
                            <td className="adm-cell-muted">{f.mode}</td>
                            <td className="adm-cell-muted">{f.duration}</td>
                            <td><strong>{f.places_restantes ?? '–'}</strong></td>
                            <td>
                              <span className="adm-badge" style={f.is_free ? { color:'#22c55e', background:'#f0fdf4', border:'1px solid #22c55e30' } : { color:'#888', background:'#f4f4f4', border:'1px solid #88888830' }}>
                                {f.is_free ? 'Oui' : 'Non'}
                              </span>
                            </td>
                            <td>
                              <div className="adm-action-group">
                                <button className="adm-btn-icon" onClick={() => setEditFormation({ ...f })} title="Modifier">
                                  <Pencil size={14} />
                                </button>
                                <button className="adm-btn-icon adm-btn-icon--red" onClick={() => deleteFormation(f.id)} title="Supprimer">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!formations.length && (
                          <tr><td colSpan={6}>
                            <Empty icon={<GraduationCap size={28} />} title="Aucune formation" />
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── CANDIDATURES ── */}
          {!loading && section === 'Candidatures' && (
            <div>
              <div className="adm-filter-bar" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {Object.entries(STATUT_MAP).map(([key, { label, color }]) => (
                    <button
                      key={key}
                      className={`adm-filter-btn ${filterStatut === key ? 'active' : ''}`}
                      style={filterStatut === key ? { borderColor: color, color, background: `${color}12` } : {}}
                      onClick={() => setFilterStatut(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  className="adm-btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', fontSize: '13px' }}
                  onClick={async () => { try { await api.exportCandidaturesCSV(token, filterStatut); } catch (e) { showToast(e.message, true); } }}
                  title="Exporter en CSV (filtre actuel)"
                >
                  <FileText size={14} /> Exporter CSV
                </button>
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr><th>Candidat</th><th>Formation</th><th>Statut</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {candidatures.map(c => (
                      <tr key={c.id}>
                        <td>
                          <div className="adm-cell-primary">{c.prenom} {c.nom}</div>
                          <div className="adm-cell-sub">{c.email}</div>
                        </td>
                        <td className="adm-cell-muted">{formations.find(f => f.id === c.formation_id)?.title || c.formation_id}</td>
                        <td><Badge statut={c.statut} /></td>
                        <td className="adm-cell-muted">{c.created_at?.slice(0, 10)}</td>
                        <td>
                          <div className="adm-action-group">
                            <button className="adm-btn-sm" onClick={() => setViewCandidature(c)} title="Voir la candidature complète"><Eye size={13} /></button>
                            {c.statut !== 'entretien' && (
                              <button className="adm-btn-sm adm-btn-sm--blue" onClick={() => updateStatut(c.id, 'entretien')}>Entretien</button>
                            )}
                            {c.statut !== 'accepte' && (
                              <button className="adm-btn-sm adm-btn-sm--green" onClick={() => updateStatut(c.id, 'accepte')}>Accepter</button>
                            )}
                            {c.statut !== 'refuse' && (
                              <button className="adm-btn-sm adm-btn-sm--red" onClick={() => updateStatut(c.id, 'refuse')}>Refuser</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!candidatures.length && (
                      <tr><td colSpan={5}>
                        <Empty icon={<Users size={28} />} title="Aucune candidature" sub="Aucun résultat pour ce filtre" />
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── CONTACTS ── */}
          {!loading && section === 'Contacts' && (
            <div>
              <div className="adm-section-header" style={{ marginBottom: '0' }}>
                <h2 className="adm-section-title">Messages Contact</h2>
                <button className="adm-btn-outline" style={{ fontSize:'12px', display:'flex', alignItems:'center', gap:'6px' }}
                  onClick={async () => { try { await api.exportContactsCSV(token, filterContactType); } catch(e) { showToast(e.message, true); } }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Exporter CSV
                </button>
              </div>
              <div className="adm-filter-bar">
                {[
                  { key: 'tous',         label: 'Tous' },
                  { key: 'general',      label: 'Question générale' },
                  { key: 'bailleur',     label: 'Partenariat' },
                  { key: 'recrutement',  label: 'Recrutement' },
                ].map(f => (
                  <button
                    key={f.key}
                    className={`adm-filter-btn ${filterContactType === f.key ? 'active' : ''}`}
                    onClick={() => setFilterContactType(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr><th>Expéditeur</th><th>Sujet</th><th>Type</th><th>Message</th><th>Date</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {contacts.map(c => (
                      <tr key={c.id}>
                        <td>
                          <div className="adm-cell-primary">{c.nom}</div>
                          <div className="adm-cell-sub">{c.email}</div>
                        </td>
                        <td className="adm-cell-muted" style={{ maxWidth: '180px' }}>{c.sujet}</td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                            background: c.type === 'candidature' ? '#e6f7d9' : c.type === 'bailleur' ? '#e8f1fe' : c.type === 'recrutement' ? '#fff3e0' : '#f4f4f4',
                            color:      c.type === 'candidature' ? '#3a7d0a' : c.type === 'bailleur' ? '#1a56c4' : c.type === 'recrutement' ? '#c06000' : '#555',
                          }}>
                            {c.type}
                          </span>
                        </td>
                        <td className="adm-cell-muted" style={{ maxWidth: '260px', whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                          {c.message?.length > 120 ? c.message.slice(0, 120) + '…' : c.message}
                        </td>
                        <td className="adm-cell-muted">{c.created_at?.slice(0, 10)}</td>
                        <td style={{ display: 'flex', gap: '6px' }}>
                          <button className="adm-btn-sm" onClick={() => setViewContact(c)} title="Lire le message">
                            <Eye size={13} />
                          </button>
                          <button className="adm-btn-sm adm-btn-sm--red" onClick={() => deleteContactMsg(c.id)}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!contacts.length && (
                      <tr><td colSpan={6}>
                        <Empty icon={<MessageSquare size={28} />} title="Aucun message" sub="Les messages du formulaire contact apparaîtront ici" />
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── MODAL CANDIDATURE COMPLÈTE ── */}
          {viewCandidature && (() => {
            const c = viewCandidature;
            const STATUT_COLORS = { en_attente: '#f59e0b', entretien: '#3b82f6', accepte: '#22c55e', refuse: '#ef4444' };
            const STATUT_LABELS = { en_attente: 'En attente', entretien: 'Entretien', accepte: 'Accepté', refuse: 'Refusé' };
            const formationLabel = formations.find(f => f.id === c.formation_id)?.title || c.formation_id;
            return (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                onClick={() => setViewCandidature(null)}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '580px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}
                  onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '17px' }}>{c.prenom} {c.nom}</div>
                      <div style={{ color: '#888', fontSize: '13px' }}>{c.email} {c.telephone ? `· ${c.telephone}` : ''}</div>
                    </div>
                    <button onClick={() => setViewCandidature(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={20} /></button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    {[
                      ['Formation', formationLabel],
                      ['Ville', c.ville || '—'],
                      ['Niveau', c.niveau || '—'],
                      ['Source', c.source || '—'],
                      ['Statut', null],
                      ['Date', c.created_at?.slice(0, 10)],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{label}</div>
                        {label === 'Statut'
                          ? <span style={{ padding: '2px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, background: `${STATUT_COLORS[c.statut]}20`, color: STATUT_COLORS[c.statut] }}>{STATUT_LABELS[c.statut] || c.statut}</span>
                          : <div style={{ fontSize: '13px', color: '#333' }}>{val}</div>
                        }
                      </div>
                    ))}
                  </div>
                  {c.motivation && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Motivation</div>
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '13px', color: '#333', background: '#f7f7f7', borderRadius: '8px', padding: '14px' }}>{c.motivation}</div>
                    </div>
                  )}
                  {c.notes_admin && (
                    <div>
                      <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Notes admin</div>
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '13px', color: '#333', background: '#fffbeb', borderRadius: '8px', padding: '14px', border: '1px solid #fcd34d' }}>{c.notes_admin}</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
                    {c.statut !== 'entretien' && <button className="adm-btn-sm adm-btn-sm--blue" onClick={() => { updateStatut(c.id, 'entretien'); setViewCandidature(p => ({...p, statut: 'entretien'})); }}>Entretien</button>}
                    {c.statut !== 'accepte'   && <button className="adm-btn-sm adm-btn-sm--green" onClick={() => { updateStatut(c.id, 'accepte');   setViewCandidature(p => ({...p, statut: 'accepte'}));   }}>Accepter</button>}
                    {c.statut !== 'refuse'    && <button className="adm-btn-sm adm-btn-sm--red"   onClick={() => { updateStatut(c.id, 'refuse');    setViewCandidature(p => ({...p, statut: 'refuse'}));    }}>Refuser</button>}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── MODAL LECTURE MESSAGE CONTACT ── */}
          {viewContact && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
              onClick={() => setViewContact(null)}>
              <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '560px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '16px' }}>{viewContact.nom}</div>
                    <div style={{ color: '#888', fontSize: '13px' }}>{viewContact.email}</div>
                  </div>
                  <button onClick={() => setViewContact(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={20} /></button>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sujet</span>
                  <div style={{ marginTop: '4px', fontWeight: 600 }}>{viewContact.sujet}</div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Message</span>
                  <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '14px', color: '#333', background: '#f7f7f7', borderRadius: '8px', padding: '14px' }}>
                    {viewContact.message}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#999' }}>
                  <span>{viewContact.created_at?.slice(0, 16).replace('T', ' à ')}</span>
                  <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                    background: viewContact.type === 'candidature' ? '#e6f7d9' : viewContact.type === 'bailleur' ? '#e8f1fe' : viewContact.type === 'recrutement' ? '#fff3e0' : '#f4f4f4',
                    color:      viewContact.type === 'candidature' ? '#3a7d0a' : viewContact.type === 'bailleur' ? '#1a56c4' : viewContact.type === 'recrutement' ? '#c06000' : '#555',
                  }}>{viewContact.type}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── PROJETS ── */}
          {!loading && section === 'Projets' && (
            editProjet ? (
              <div className="adm-card">
                <div className="adm-card__header">
                  <button className="adm-back-btn" onClick={() => setEditProjet(null)}>← Retour</button>
                  <h2 className="adm-card__title">{editProjet.id ? `Modifier : ${editProjet.title}` : 'Nouveau projet'}</h2>
                </div>
                <div className="adm-form-grid">
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Titre</label>
                    <input className="adm-input" value={editProjet.title || ''} onChange={e => setEditProjet(p => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div className="adm-field-hint" style={{ gridColumn: '1 / -1', margin: '0 0 8px' }}>
                    Ces champs alimentent les <strong>cards</strong> sur la page liste et la homepage.
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Description courte <span className="adm-label-hint">(card homepage)</span></label>
                    <textarea className="adm-input" rows={2} value={editProjet.description || ''} onChange={e => setEditProjet(p => ({ ...p, description: e.target.value }))} placeholder="Phrase d'accroche affichée sur la card de la homepage" />
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Description longue <span className="adm-label-hint">(card page liste)</span></label>
                    <textarea className="adm-input" rows={3} value={editProjet.long_description || ''} onChange={e => setEditProjet(p => ({ ...p, long_description: e.target.value }))} placeholder="Description complète affichée dans la card de la page /projets" />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Partenaire</label>
                    <input className="adm-input" value={editProjet.partner || ''} onChange={e => setEditProjet(p => ({ ...p, partner: e.target.value }))} />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Année</label>
                    <input className="adm-input" value={editProjet.year || ''} onChange={e => setEditProjet(p => ({ ...p, year: e.target.value }))} />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Lieu / Ville <span className="adm-label-hint">(card liste)</span></label>
                    <input className="adm-input" value={editProjet.location || ''} onChange={e => setEditProjet(p => ({ ...p, location: e.target.value }))} placeholder="Abidjan, Côte d'Ivoire" />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Durée <span className="adm-label-hint">(card liste)</span></label>
                    <input className="adm-input" value={editProjet.duration || ''} onChange={e => setEditProjet(p => ({ ...p, duration: e.target.value }))} placeholder="12 semaines" />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Statut</label>
                    <select className="adm-input" value={editProjet.status || 'en_cours'} onChange={e => setEditProjet(p => ({ ...p, status: e.target.value }))}>
                      <option value="en_cours">En cours</option>
                      <option value="termine">Terminé</option>
                    </select>
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Participants</label>
                    <input className="adm-input" type="number" value={editProjet.participants ?? 0} onChange={e => setEditProjet(p => ({ ...p, participants: +e.target.value }))} />
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Tags formations <span className="adm-label-hint">(card liste — séparés par virgule)</span></label>
                    <input className="adm-input" value={Array.isArray(editProjet.tags) ? editProjet.tags.join(', ') : (editProjet.tags || '')} onChange={e => setEditProjet(p => ({ ...p, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))} placeholder="IA, Fullstack, Data Analytics" />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Date candidature</label>
                    <input className="adm-input" type="date" value={(editProjet.candidature_date || '').slice(0, 10)} onChange={e => setEditProjet(p => ({ ...p, candidature_date: e.target.value + 'T00:00:00' }))} />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Mode candidature</label>
                    <select className="adm-input" value={editProjet.candidature_mode || 'opening'} onChange={e => setEditProjet(p => ({ ...p, candidature_mode: e.target.value }))}>
                      <option value="opening">Ouverture</option>
                      <option value="closing">Clôture</option>
                    </select>
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Image du projet</label>
                    <div className="adm-input-row">
                      <input className="adm-input" value={editProjet.image_url || ''} onChange={e => setEditProjet(p => ({ ...p, image_url: e.target.value }))} placeholder="https://…/image.jpg" />
                      <button type="button" className="adm-btn-media" onClick={() => openMediaPicker(url => setEditProjet(p => ({ ...p, image_url: url })))}>
                        <Library size={14} /> Médiathèque
                      </button>
                    </div>
                    {editProjet.image_url && <img src={editProjet.image_url} alt="" className="adm-img-preview" />}
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Lien externe <span className="adm-label-hint">(site du projet, rapport…)</span></label>
                    <div className="adm-input-row">
                      <span className="adm-input-icon"><LinkIcon size={14} /></span>
                      <input className="adm-input adm-input--icon" value={editProjet.lien_externe || ''} onChange={e => setEditProjet(p => ({ ...p, lien_externe: e.target.value }))} placeholder="https://…" />
                    </div>
                  </div>
                </div>
                <div className="adm-card__actions">
                  <button className="adm-btn-primary" onClick={() => saveProjet(editProjet)}>Enregistrer</button>
                  <button className="adm-btn-outline" onClick={() => setEditProjet(null)}>Annuler</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="adm-section-header">
                  <h2 className="adm-section-title">Cards Projets <span className="adm-section-sub">— liste & homepage</span></h2>
                  <button className="adm-btn-primary" onClick={() => setEditProjet({ title: '', description: '', long_description: '', partner: '', year: new Date().getFullYear().toString(), location: "Abidjan, Côte d'Ivoire", duration: '', status: 'en_cours', participants: 0, tags: [], image_url: '', lien_externe: '', candidature_date: '', candidature_mode: 'opening' })}>
                    + Nouveau projet
                  </button>
                </div>
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr><th>Projet</th><th>Statut</th><th>Année</th><th>Participants</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {projets.map(p => (
                        <tr key={p.id}>
                          <td>
                            <div className="adm-cell-primary">{p.title}</div>
                            <div className="adm-cell-sub">{p.partner}</div>
                          </td>
                          <td>
                            <span className="adm-badge" style={p.status === 'en_cours' ? { color:'#3b82f6', background:'#eff6ff', border:'1px solid #3b82f630' } : { color:'#22c55e', background:'#f0fdf4', border:'1px solid #22c55e30' }}>
                              {p.status === 'en_cours' ? 'En cours' : 'Terminé'}
                            </span>
                          </td>
                          <td className="adm-cell-muted">{p.year}</td>
                          <td><strong>{p.participants}</strong></td>
                          <td>
                            <button className="adm-btn-icon" title="Modifier" onClick={() => setEditProjet({ ...p })}>
                              <Pencil size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!projets.length && (
                        <tr><td colSpan={5}>
                          <Empty icon={<FolderKanban size={28} />} title="Aucun projet" />
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}

          {/* ── PARTENAIRES ── */}
          {!loading && section === 'Partenaires' && (
            editPartenaire ? (
              /* Formulaire ajout / modification */
              <div className="adm-card adm-card--narrow">
                <div className="adm-card__header">
                  <button className="adm-back-btn" onClick={() => setEditPartenaire(null)}>← Retour</button>
                  <h2 className="adm-card__title">{editPartenaire.id ? `Modifier : ${editPartenaire.nom}` : 'Nouveau partenaire'}</h2>
                </div>
                <div className="adm-form-grid">
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Nom *</label>
                    <input className="adm-input" value={editPartenaire.nom || ''} onChange={e => setEditPartenaire(p => ({ ...p, nom: e.target.value }))} placeholder="GIZ, Atos, FORPRODE…" />
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Logo mode clair <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>(fond coloré / light)</span></label>
                    <div className="adm-input-row">
                      <input className="adm-input" value={editPartenaire.logo_url || ''} onChange={e => setEditPartenaire(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://…/logo-light.png" />
                      <button type="button" className="adm-btn-media" onClick={() => openMediaPicker(url => setEditPartenaire(p => ({ ...p, logo_url: url })))}>
                        <Library size={14} /> Médiathèque
                      </button>
                    </div>
                    {editPartenaire.logo_url && <img src={editPartenaire.logo_url} alt="" className="adm-img-preview adm-img-preview--small" style={{ background: '#8BC53F', padding: '4px', borderRadius: '6px' }} />}
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Logo mode sombre <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>(fond sombre / dark)</span></label>
                    <div className="adm-input-row">
                      <input className="adm-input" value={editPartenaire.logo_dark_url || ''} onChange={e => setEditPartenaire(p => ({ ...p, logo_dark_url: e.target.value }))} placeholder="https://…/logo-dark.png" />
                      <button type="button" className="adm-btn-media" onClick={() => openMediaPicker(url => setEditPartenaire(p => ({ ...p, logo_dark_url: url })))}>
                        <Library size={14} /> Médiathèque
                      </button>
                    </div>
                    <div className="adm-field-hint">Les deux logos s'affichent selon le thème choisi par le visiteur. Si "mode sombre" est vide, le logo clair sera utilisé à la place.</div>
                    {editPartenaire.logo_dark_url && <img src={editPartenaire.logo_dark_url} alt="" className="adm-img-preview adm-img-preview--small" style={{ background: '#243828', padding: '4px', borderRadius: '6px' }} />}
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Site web (URL)</label>
                    <input className="adm-input" value={editPartenaire.site_url || ''} onChange={e => setEditPartenaire(p => ({ ...p, site_url: e.target.value }))} placeholder="https://…" />
                  </div>
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Description</label>
                    <textarea className="adm-input" rows={2} value={editPartenaire.description || ''} onChange={e => setEditPartenaire(p => ({ ...p, description: e.target.value }))} />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Ordre d'affichage</label>
                    <input className="adm-input" type="number" value={editPartenaire.ordre ?? 0} onChange={e => setEditPartenaire(p => ({ ...p, ordre: +e.target.value }))} />
                  </div>
                </div>
                <div className="adm-card__actions">
                  <button className="adm-btn-primary" onClick={() => { if (!editPartenaire.nom?.trim()) { alert('Le nom est requis.'); return; } savePartenaire(editPartenaire); }}>
                    {editPartenaire.id ? 'Enregistrer' : 'Ajouter le partenaire'}
                  </button>
                  <button className="adm-btn-outline" onClick={() => setEditPartenaire(null)}>Annuler</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="adm-section-header">
                  <h2 className="adm-section-title">Partenaires <span className="adm-section-sub">— logos homepage</span></h2>
                  <button className="adm-btn-primary" onClick={() => setEditPartenaire({ nom: '', site_url: '', description: '', ordre: partenaires.length + 1 })}>
                    <Plus size={15} /> Ajouter
                  </button>
                </div>
                <div className="adm-card">
                  {partenaires.map((p, i) => (
                    <div key={p.id} className="adm-list-row" style={i > 0 ? { borderTop: '1px solid #f0f0f0' } : {}}>
                      {p.logo_url
                        ? <img src={p.logo_url} alt={p.nom} style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: '#f4f4f4', padding: 2, flexShrink: 0 }} />
                        : <div className="adm-list-row__avatar">{p.nom?.[0] || '?'}</div>
                      }
                      <div style={{ flex: 1 }}>
                        <div className="adm-cell-primary">{p.nom}</div>
                        <div className="adm-cell-sub">
                          {p.logo_dark_url ? <span style={{ color:'#8b5cf6', fontSize:11, marginRight:8 }}>● logo dark</span> : <span style={{ color:'#aaa', fontSize:11, marginRight:8 }}>○ pas de logo dark</span>}
                          {p.site_url && <span>{p.site_url}</span>}
                        </div>
                      </div>
                      <div className="adm-action-group">
                        <button className="adm-btn-icon" title="Modifier" onClick={() => setEditPartenaire({ ...p })}>
                          <Pencil size={13} />
                        </button>
                        <button className="adm-btn-icon adm-btn-icon--red" title="Supprimer" onClick={() => removePartenaire(p.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {!partenaires.length && (
                    <Empty icon={<Handshake size={28} />} title="Aucun partenaire" />
                  )}
                </div>
              </div>
            )
          )}

          {/* ── ACTUALITÉS ── */}
          {!loading && section === 'Actualités' && (
            editArticle ? (
              <ArticleEditor
                article={editArticle}
                onSave={saveArticle}
                onCancel={() => setEditArticle(null)}
                onPickImage={openMediaPicker}
              />
            ) : (
              <div>
                <div className="adm-section-header">
                  <h2 className="adm-section-title">Articles & Actualités</h2>
                  <button className="adm-btn-primary" onClick={() => setEditArticle({})}>
                    <Plus size={15} /> Nouvel article
                  </button>
                </div>
                {articles.length ? (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr><th>Titre</th><th>Catégorie</th><th>Statut</th><th>Date</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {articles.map(a => (
                          <tr key={a.id}>
                            <td>
                              <div className="adm-cell-primary">{a.titre}</div>
                              <div className="adm-cell-sub">{a.resume?.slice(0, 60)}{a.resume?.length > 60 ? '…' : ''}</div>
                            </td>
                            <td className="adm-cell-muted">{a.categorie || '–'}</td>
                            <td><Badge statut={a.publie ? 'accepte' : 'en_attente'} /></td>
                            <td className="adm-cell-muted">{(a.published_at || a.created_at)?.slice(0, 10)}</td>
                            <td>
                              <div className="adm-action-group">
                                <button className="adm-btn-icon" title="Modifier" onClick={() => setEditArticle({ ...a })}>
                                  <Pencil size={14} />
                                </button>
                                <button className="adm-btn-icon adm-btn-icon--red" title="Supprimer" onClick={() => removeArticle(a.id)}>
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="adm-card">
                    <Empty
                      icon={<Newspaper size={32} />}
                      title="Aucun article"
                      sub="Créez votre premier article pour alimenter la section Actualités."
                      action={
                        <button className="adm-btn-primary" style={{ marginTop: '16px' }} onClick={() => setEditArticle({})}>
                          <Plus size={14} /> Créer un article
                        </button>
                      }
                    />
                  </div>
                )}
              </div>
            )
          )}

          {/* ── UTILISATEURS ── */}
          {!loading && section === 'Utilisateurs' && (
            <div>
              {editUser ? (
                <UserForm user={editUser} onSave={saveUser} onCancel={() => setEditUser(null)} />
              ) : (
                <div>
                  <div className="adm-section-header">
                    <h2 className="adm-section-title">Utilisateurs</h2>
                    <button className="adm-btn-primary" onClick={() => setEditUser({})}>
                      <Plus size={14} /> Nouvel admin
                    </button>
                  </div>
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr><th>Nom / Email</th><th>Rôle</th><th>Statut</th><th>Dernier login</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.id}>
                            <td>
                              <div className="adm-cell-primary">{u.nom || '—'}</div>
                              <div className="adm-cell-muted">{u.email}</div>
                            </td>
                            <td>
                              <span className="adm-badge" style={u.role === 'super_admin'
                                ? { color:'#7c3aed', background:'#f5f3ff', border:'1px solid #7c3aed30' }
                                : { color:'#3b82f6', background:'#eff6ff', border:'1px solid #3b82f630' }}>
                                {u.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                              </span>
                            </td>
                            <td>
                              <span className="adm-badge" style={u.actif
                                ? { color:'#22c55e', background:'#f0fdf4', border:'1px solid #22c55e30' }
                                : { color:'#888', background:'#f4f4f4', border:'1px solid #88888830' }}>
                                {u.actif ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td className="adm-cell-muted">{u.last_login ? new Date(u.last_login).toLocaleDateString('fr-FR') : '—'}</td>
                            <td style={{ display:'flex', gap:6 }}>
                              <button className="adm-btn-icon" title="Modifier" onClick={() => setEditUser({ ...u })}>
                                <Pencil size={14} />
                              </button>
                              <button className="adm-btn-icon" title={u.actif ? 'Désactiver' : 'Activer'}
                                onClick={() => toggleUserActif(u)}>
                                {u.actif ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!users.length && (
                          <tr><td colSpan={5}>
                            <Empty icon={<UserCog size={28} />} title="Aucun utilisateur" />
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── JOURNAL D'ACTIVITÉ ── */}
          {!loading && section === 'Journal' && (
            <div>
              <div className="adm-section-header">
                <h2 className="adm-section-title">Journal d'activité</h2>
                <button className="adm-btn-outline" onClick={() => loadSection('Journal')}>
                  Actualiser
                </button>
              </div>
              {auditLog.length === 0 ? (
                <div className="adm-empty" style={{ marginTop: 40 }}>
                  <div className="adm-empty__icon"><ScrollText size={36} /></div>
                  <div className="adm-empty__title">Aucune activité enregistrée</div>
                  <div className="adm-empty__sub">Les actions des administrateurs apparaîtront ici.</div>
                </div>
              ) : (
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr><th>Date</th><th>Admin</th><th>Action</th><th>Cible</th><th>Détails</th></tr>
                    </thead>
                    <tbody>
                      {auditLog.map(log => (
                        <tr key={log.id}>
                          <td className="adm-cell-muted" style={{ whiteSpace:'nowrap' }}>
                            {new Date(log.created_at).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                          </td>
                          <td>
                            <div className="adm-cell-primary">{log.admin_nom || '—'}</div>
                            <div className="adm-cell-muted">{log.admin_email}</div>
                          </td>
                          <td><span className="adm-audit-action">{log.action}</span></td>
                          <td className="adm-cell-muted">{log.table_name}{log.record_id ? ` #${log.record_id}` : ''}</td>
                          <td className="adm-cell-muted" style={{ fontSize:11 }}>
                            {log.details ? JSON.stringify(log.details).slice(0, 60) + '…' : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── MÉDIATHÈQUE ── */}
          {!loading && section === 'Médiathèque' && (
            <div>
              <div className="adm-section-header" style={{ justifyContent: 'space-between' }}>
                <h2 className="adm-section-title">Médiathèque</h2>
                <label className="adm-btn-primary adm-btn-upload" style={{ cursor: 'pointer' }}>
                  <Upload size={14} /> Uploader une image
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }}
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        const res = await api.uploadFile(file, token);
                        setUploads(p => [{ url: res.url, filename: res.filename }, ...p]);
                        showToast('Image uploadée ✓');
                      } catch (err) { showToast(err.message, true); }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {uploads.length === 0 ? (
                <div className="adm-empty" style={{ marginTop: 40 }}>
                  <div className="adm-empty__icon"><Library size={36} /></div>
                  <div className="adm-empty__title">Aucune image uploadée</div>
                  <div className="adm-empty__sub">Utilisez le bouton "Uploader une image" pour ajouter des fichiers.</div>
                </div>
              ) : (
                <div className="adm-media-section-grid">
                  {uploads.map(f => (
                    <MediaCard key={f.filename} file={f} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── CMS FORMATIONS ── */}
          {!loading && section === 'CMS Formations' && (
            <CmsFormationsEditor
              editCmsFormation={editCmsFormation}
              setEditCmsFormation={setEditCmsFormation}
              formations={formations}
              saveCmsFormation={saveCmsFormation}
              staticFormationsData={staticFormationsData}
            />
          )}
          {/* ── PAGES CMS ── */}
          {!loading && section === 'Pages' && (
            <div>
              {!currentPageId ? (
                /* Liste des pages */
                <div>
                  <div className="adm-section-header">
                    <h2 className="adm-section-title">Pages du site</h2>
                    <span className="adm-section-sub">Cliquez sur "Éditer" pour modifier les sections d'une page</span>
                  </div>
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead><tr><th>Slug</th><th>Titre</th><th>Statut</th><th>Meta title</th><th>Action</th></tr></thead>
                      <tbody>
                        {pages.map(p => (
                          <tr key={p.id}>
                            <td className="adm-cell-muted">/{p.slug}</td>
                            <td className="adm-cell-primary">{p.titre}</td>
                            <td>
                              <span style={{ color: p.publie ? '#22c55e' : '#aaa', fontSize: 12 }}>
                                {p.publie ? '● Publié' : '○ Masqué'}
                              </span>
                            </td>
                            <td className="adm-cell-muted" style={{ fontSize: 11 }}>{p.meta_title || '—'}</td>
                            <td>
                              <button className="adm-btn-sm" onClick={() => {
                                setCurrentPageId(p.id);
                                loadPageSections(p.id);
                              }}>
                                <Pencil size={12} /> Éditer sections
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!pages.length && (
                          <tr><td colSpan={5}>
                            <Empty icon={<LayoutTemplate size={28} />} title="Aucune page" sub="Exécutez schema-cms.sql et seed-cms.sql" />
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                /* Éditeur de sections */
                <div>
                  <div className="adm-section-header">
                    <button className="adm-back-btn" onClick={() => setCurrentPageId(null)}>← Pages</button>
                    <h2 className="adm-section-title">
                      {pages.find(p => p.id === currentPageId)?.titre}
                      <span className="adm-section-sub" style={{ marginLeft: 10 }}>
                        /{pages.find(p => p.id === currentPageId)?.slug}
                      </span>
                    </h2>
                  </div>

                  <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={pageSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                      <div className="adm-sections-list">
                        {pageSections.map((sec, i) => (
                          <SortableSectionRow
                            key={sec.id}
                            section={sec}
                            expanded={expandedSection === sec.id}
                            onToggle={() => setExpandedSection(p => p === sec.id ? null : sec.id)}
                            onSave={saveSectionContent}
                            onDelete={() => removeSectionFromPage(sec.id)}
                            onMoveUp={() => moveSectionDir(i, -1)}
                            onMoveDown={() => moveSectionDir(i, 1)}
                            onToggleActif={() => toggleSectionActif(sec)}
                            isFirst={i === 0}
                            isLast={i === pageSections.length - 1}
                            onPickImage={openMediaPicker}
                          />
                        ))}
                        {!pageSections.length && (
                          <Empty icon={<LayoutTemplate size={28} />} title="Aucune section" sub="Ajoutez votre première section ci-dessous" />
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>

                  <div style={{ marginTop: 16 }}>
                    <button className="adm-btn-outline" onClick={() => setAddSectionModal(true)}>
                      <Plus size={14} /> Ajouter une section
                    </button>
                  </div>
                </div>
              )}

              {/* Modal ajout section */}
              {addSectionModal && (
                <div className="adm-mpicker-overlay" onClick={e => e.target === e.currentTarget && setAddSectionModal(false)}>
                  <div className="adm-add-section-modal">
                    <div className="adm-mpicker-header">
                      <span className="adm-mpicker-title">Ajouter une section</span>
                      <button className="adm-mpicker-close" onClick={() => setAddSectionModal(false)}><X size={16} /></button>
                    </div>
                    {[
                      { group: 'base', label: 'Sections de base' },
                      { group: 'page', label: 'Sections spécifiques pages' },
                      { group: 'dyn',  label: 'Contenus dynamiques' },
                    ].map(g => (
                      <div key={g.group} style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 4px 8px' }}>
                          {g.label}
                        </div>
                        <div className="adm-section-types-grid">
                          {SECTION_TYPES.filter(t => t.group === g.group).map(t => (
                            <button key={t.type} className="adm-section-type-btn" onClick={() => addSectionToPage(t.type)}>
                              <t.Icon size={20} />
                              <span>{t.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── NAVIGATION ── */}
          {!loading && section === 'Navigation' && (
            <div>
              <div className="adm-section-header">
                <h2 className="adm-section-title">Navigation</h2>
                <button className="adm-btn-primary" onClick={() => setAddNavForm({ parentId: null })}>
                  <Plus size={14} /> Ajouter un item
                </button>
              </div>

              <div className="adm-nav-edit-list">
                {navItems.map((item, i) => (
                  <NavItemRow
                    key={item.id}
                    item={item}
                    level={0}
                    onSave={saveNavItem}
                    onDelete={deleteNavItemAction}
                    onMoveUp={() => moveNavItem(navItems, i, -1)}
                    onMoveDown={() => moveNavItem(navItems, i, 1)}
                    isFirst={i === 0}
                    isLast={i === navItems.length - 1}
                    onAddChild={(parentId) => setAddNavForm({ parentId })}
                  />
                ))}
                {!navItems.length && (
                  <Empty icon={<Globe size={28} />} title="Aucun item de navigation" sub="Ajoutez votre premier item ci-dessus" />
                )}
              </div>

              {/* Formulaire d'ajout */}
              {addNavForm && (
                <div className="adm-nav-add-form">
                  <div className="adm-nav-add-form__title">
                    {addNavForm.parentId ? (
                      <><CornerDownRight size={14} /> Nouveau sous-menu</>
                    ) : (
                      <><Globe size={14} /> Nouvel item racine</>
                    )}
                  </div>
                  <AddNavForm
                    onSubmit={({ label, href }) => createNavItemAction({ label, href, parentId: addNavForm.parentId })}
                    onCancel={() => setAddNavForm(null)}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── FOOTER ── */}
          {!loading && section === 'Footer' && (
            <div>
              <div className="adm-section-header">
                <h2 className="adm-section-title">Footer</h2>
                <span className="adm-section-sub">Gérez les colonnes et le contenu du pied de page</span>
              </div>
              <div className="adm-sections-list">
                {footerSections.map((sec, i) => (
                  <FooterSectionRow
                    key={sec.id}
                    section={sec}
                    expanded={expandedFooter === sec.id}
                    onToggle={() => setExpandedFooter(p => p === sec.id ? null : sec.id)}
                    onSave={saveFooterSection}
                    onDelete={() => deleteFooterSection(sec.id)}
                    onMoveUp={() => moveFooterSection(i, -1)}
                    onMoveDown={() => moveFooterSection(i, 1)}
                    onToggleActif={() => toggleFooterActif(sec)}
                    isFirst={i === 0}
                    isLast={i === footerSections.length - 1}
                  />
                ))}
                {!footerSections.length && (
                  <Empty icon={<Layers size={28} />} title="Aucune section footer" sub="Utilisez les boutons ci-dessous pour créer le footer" />
                )}
              </div>
              <div className="adm-card" style={{ marginTop: '20px' }}>
                <div className="adm-label" style={{ marginBottom: '10px' }}>Ajouter une section</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Object.entries(FOOTER_TYPE_LABELS).map(([type, label]) => (
                    <button key={type} className="adm-btn-sm" onClick={() => addFooterSection(type)}>
                      + {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── COMPTEUR FORPRODE ── */}
          {!loading && section === 'Compteur FORPRODE' && (
            <div>
              <div className="adm-section-header">
                <h2 className="adm-section-title">Gestion du compteur FORPRODE 40 IA</h2>
              </div>
              <div className="adm-card adm-card--narrow">
                <div className="adm-field">
                  <label className="adm-label">Type de compteur</label>
                  <select
                    className="adm-input"
                    value={editSettings.forprode_mode || 'opening'}
                    onChange={e => setEditSettings({ ...editSettings, forprode_mode: e.target.value })}
                  >
                    <option value="opening">Ouverture des candidatures dans X jours</option>
                    <option value="closing">Clôture des candidatures dans X jours</option>
                  </select>
                </div>
                <div className="adm-field">
                  <label className="adm-label">Date cible</label>
                  <input
                    className="adm-input" type="date"
                    value={(editSettings.forprode_date || '').slice(0, 10)}
                    onChange={e => setEditSettings({ ...editSettings, forprode_date: e.target.value + 'T00:00:00' })}
                  />
                </div>
                <div className="adm-countdown-preview">
                  <div className="adm-countdown-preview__label">Aperçu</div>
                  <div className="adm-countdown-preview__msg">
                    {editSettings.forprode_mode === 'closing'
                      ? '⏳ Clôture des candidatures dans'
                      : '⏳ Ouverture des candidatures dans'}{' '}
                    <strong>{joursForprode()} jours</strong>
                  </div>
                </div>
                <button className="adm-btn-primary" onClick={saveCountdown}>
                  Enregistrer le compteur
                </button>
              </div>
            </div>
          )}

          {/* ── PARAMÈTRES ── */}
          {!loading && section === 'Paramètres' && (
            <div>
              <div className="adm-section-header">
                <h2 className="adm-section-title">Paramètres généraux du site</h2>
              </div>
              <div className="adm-card">
                <div className="adm-form-grid">
                  {[
                    { key: 'site_nom',       label: 'Nom du site' },
                    { key: 'site_email',     label: 'Email de contact' },
                    { key: 'site_telephone', label: 'Téléphone' },
                    { key: 'site_ville',     label: 'Ville' },
                  ].map(f => (
                    <div key={f.key} className="adm-field">
                      <label className="adm-label">{f.label}</label>
                      <input
                        className="adm-input"
                        value={editSettings[f.key] || ''}
                        onChange={e => setEditSettings({ ...editSettings, [f.key]: e.target.value })}
                      />
                    </div>
                  ))}
                  <div className="adm-field">
                    <label className="adm-label">Langue par défaut</label>
                    <select
                      className="adm-input"
                      value={editSettings.site_langue || 'fr'}
                      onChange={e => setEditSettings({ ...editSettings, site_langue: e.target.value })}
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>
                <div className="adm-card__actions">
                  <button className="adm-btn-primary" onClick={saveSettingsForm}>
                    Enregistrer les paramètres
                  </button>
                </div>
              </div>

              {/* Bannière d'annonce */}
              <div className="adm-card" style={{ marginTop:'20px' }}>
                <div className="adm-card__header">
                  <h3 className="adm-card__title" style={{ fontSize:'14px' }}>Bannière d'annonce</h3>
                  <label className="adm-toggle">
                    <input type="checkbox"
                      checked={editSettings.banner_active === '1'}
                      onChange={e => setEditSettings({ ...editSettings, banner_active: e.target.checked ? '1' : '0' })}
                    />
                    <span className="adm-toggle__track" />
                  </label>
                </div>
                <p style={{ fontSize:'12px', color:'rgba(255,255,255,.4)', marginBottom:'16px', lineHeight:1.55 }}>
                  Affiche une bannière en haut du site (sous la navbar). Fermable par le visiteur.
                </p>
                <div className="adm-form-grid">
                  <div className="adm-field adm-field--full">
                    <label className="adm-label">Texte de la bannière</label>
                    <input className="adm-input" placeholder="Ex : Les candidatures FORPRODE 40 IA sont ouvertes !"
                      value={editSettings.banner_text || ''}
                      onChange={e => setEditSettings({ ...editSettings, banner_text: e.target.value })}
                    />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Lien (optionnel)</label>
                    <input className="adm-input" type="url" placeholder="https://… ou /candidater"
                      value={editSettings.banner_link || ''}
                      onChange={e => setEditSettings({ ...editSettings, banner_link: e.target.value })}
                    />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Style</label>
                    <select className="adm-input"
                      value={editSettings.banner_type || 'info'}
                      onChange={e => setEditSettings({ ...editSettings, banner_type: e.target.value })}
                    >
                      <option value="info">Sombre (fond TTA)</option>
                      <option value="promo">Promo (dégradé vert)</option>
                      <option value="warning">Attention (fond jaune)</option>
                      <option value="success">Succès (fond vert clair)</option>
                    </select>
                  </div>
                </div>
                <div className="adm-card__actions" style={{ marginTop:'16px' }}>
                  <button className="adm-btn-primary" onClick={saveSettingsForm}>Enregistrer</button>
                </div>
              </div>

              {/* Lien Google Form candidature */}
              <div className="adm-card" style={{ marginTop:'20px' }}>
                <div className="adm-card__header">
                  <h3 className="adm-card__title" style={{ fontSize:'14px' }}>Lien de candidature — programmes actifs</h3>
                </div>
                <p style={{ fontSize:'12px', color:'rgba(255,255,255,.4)', marginBottom:'16px', lineHeight:1.55 }}>
                  URL Google Form affichée sur la page <code style={{ background:'rgba(255,255,255,.06)', padding:'1px 5px', borderRadius:'4px', fontSize:'11px' }}>/candidater</code> pour le programme en cours (FORPRODE 40 IA). Laisser vide pour afficher "Ouverture prochaine".
                </p>
                <div className="adm-field">
                  <label className="adm-label">URL Google Form — FORPRODE 40 IA <span className="adm-label-hint">ou programme en cours</span></label>
                  <input
                    className="adm-input"
                    type="url"
                    placeholder="https://docs.google.com/forms/d/e/..."
                    value={editSettings.forprode_candidature_link || ''}
                    onChange={e => setEditSettings({ ...editSettings, forprode_candidature_link: e.target.value })}
                  />
                </div>
                {(editSettings.forprode_candidature_link || settings.forprode_candidature_link) && (
                  <div style={{ marginTop:'8px', fontSize:'12px', color:'rgba(255,255,255,.35)' }}>
                    Lien actuel : <a href={editSettings.forprode_candidature_link || settings.forprode_candidature_link} target="_blank" rel="noreferrer" style={{ color:'#8BC53F', wordBreak:'break-all' }}>{editSettings.forprode_candidature_link || settings.forprode_candidature_link}</a>
                  </div>
                )}
                <div className="adm-card__actions" style={{ marginTop:'16px' }}>
                  <button className="adm-btn-primary" onClick={saveSettingsForm}>
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
